import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useScrollReveal from "@/lib/useScrollReveal";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import FloorPlanMockup from "@/components/FloorPlanMockup";
import ProntoLogo from "@/components/ProntoLogo";
import {
  ArrowRight, ArrowSquareOut, Plus, Minus, Sun, Palette, Ruler, Broom, Camera,
  PencilSimpleLine, Sparkle, House, FilePdf, ShareNetwork, Calendar,
  ShieldCheck, Storefront, ChartBar, Lock, Flag, CheckCircle, ChatCircleDots, ChatsCircle,
  Microphone, GoogleLogo, AppleLogo, Calculator, Armchair, Eye, UploadSimple, Spinner,
  Stack, AddressBook, WhatsappLogo, ArrowDown, QrCode, FilmStrip,
} from "@phosphor-icons/react";

import HeroAgendaMockup from "@/pages/landing/HeroAgendaMockup";import LandingFooter from "@/pages/landing/LandingFooter";
import LandingFAQ from "@/pages/landing/LandingFAQ";
import BeforeAfterBagno from "@/pages/landing/BeforeAfterBagno";
import LandingPricingTeaser from "@/pages/landing/LandingPricingTeaser";
import ProntoVsAltri from "@/pages/landing/ProntoVsAltri";
import HeroSocialMockup from "@/pages/landing/HeroSocialMockup";
import SocialPostsStrip from "@/pages/landing/SocialPostsStrip";
import HeroContattiMockup from "@/pages/landing/HeroContattiMockup";
import ContactsMatchMockup from "@/pages/landing/ContactsMatchMockup";
import HeroMessaggiMockup from "@/pages/landing/HeroMessaggiMockup";
import HeroStimaMockup from "@/pages/landing/HeroStimaMockup";
import HeroAssistantMockup from "@/pages/landing/HeroAssistantMockup";
import HeroVolantinoMockup from "@/pages/landing/HeroVolantinoMockup";
import HeroVolantinoFlip from "@/pages/landing/HeroVolantinoFlip";
import VolantinoFlipPreview from "@/components/staticTemplate/VolantinoFlipPreview";
import HeroSopralluogoMockup from "@/pages/landing/HeroSopralluogoMockup";
import HeroMandatiMockup from "@/pages/landing/HeroMandatiMockup";
import HeroVetrinaMockup from "@/pages/landing/HeroVetrinaMockup";
// HeroMarketplaceMockup non più usato sulla landing (Mossa 2 disabilitata iter 239).
// Il componente resta nel codice perché ancora referenziato altrove in caso di rollout futuro.
import CrmCompat from "@/pages/landing/CrmCompat";
import MacchinaMandati from "@/pages/landing/MacchinaMandati";
import AstaInversa from "@/pages/landing/AstaInversa";
import ZeroSbattimento from "@/pages/landing/ZeroSbattimento";
import ScrollProgress from "@/components/ScrollProgress";
import StaticTemplatePreview, { LAYOUTS, COLORS } from "@/components/StaticTemplatePreview";
import { CANONICAL_COLOR_ENTRIES, CANONICAL_COLOR_KEYS } from "@/components/staticTemplate/tokens";

const HERO_BEFORE = "/landing/before.jpg";
const HERO_AFTER = "/landing/after.jpg";
const STAGING_EMPTY = "/landing/staging-empty.jpg";
const STAGING_FURNISHED = "/landing/staging-furnished.jpg";


function ListingShowcase() {
  // Cinematic flow: l'agente sceglie "scrivi o parla" → seleziona "parla" →
  // vocale sgrammaticato e naturale → fase di "scrittura magica" → annuncio
  // professionale. Vocale completo per dare la sensazione di un agente vero
  // che dimentica le parole, si corregge, esita ("allora, eh… c'è anche la
  // cantina"). Loop ~17s (più scattante della v iniziale, più naturale della
  // v ultra-corta).
  const VOCALE = "Allora, eh, trilocale ristrutturato in Brera, due bagni, parquet originale, soffitti a cassettoni… circa 100 metri, 3 locali, quarto piano, ascensore, c'è anche la cantina… ottocentonovantamila, sì, ottocentonovanta. Pubblicalo.";
  const TITLE = "Trilocale ristrutturato, Brera";
  const TARGET_PRICE = 890000;
  const PORTALS = ["Idealista", "Immobiliare.it"];

  // -1 mode-picker · 0 vocale-typing · 1 transform · 2 photo · 3 title · 4 price · 5 portals · 6 hold
  const [phase, setPhase] = useState(-1);
  const [vocaleTyped, setVocaleTyped] = useState("");
  const [titleTyped, setTitleTyped] = useState("");
  const [counter, setCounter] = useState(0);
  const [loopId, setLoopId] = useState(0);

  useEffect(() => {
    const ts = [];
    setPhase(-1); setVocaleTyped(""); setTitleTyped(""); setCounter(0);
    /* eslint-disable react-hooks/set-state-in-effect */
    ts.push(setTimeout(() => setPhase(0),  1600));
    ts.push(setTimeout(() => setPhase(1),  6500));
    ts.push(setTimeout(() => setPhase(2),  9000));
    ts.push(setTimeout(() => setPhase(3),  9700));
    ts.push(setTimeout(() => setPhase(4), 11000));
    ts.push(setTimeout(() => setPhase(5), 12000));
    ts.push(setTimeout(() => setLoopId((x) => x + 1), 18500));
    /* eslint-enable react-hooks/set-state-in-effect */
    return () => ts.forEach(clearTimeout);
  }, [loopId]);

  // Vocale typewriter (phase 0) — 22ms/char (più veloce, su richiesta utente)
  useEffect(() => {
    if (phase !== 0) return;
    let i = 0;
    setVocaleTyped("");
    const id = setInterval(() => {
      i += 1;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setVocaleTyped(VOCALE.slice(0, i));
      if (i >= VOCALE.length) clearInterval(id);
    }, 22);
    return () => clearInterval(id);
  }, [phase]);

  // Title typewriter (phase 3) — 28ms/char (più veloce)
  useEffect(() => {
    if (phase !== 3) return;
    let i = 0;
    setTitleTyped("");
    const id = setInterval(() => {
      i += 1;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTitleTyped(TITLE.slice(0, i));
      if (i >= TITLE.length) clearInterval(id);
    }, 28);
    return () => clearInterval(id);
  }, [phase]);

  // Price counter — easeOutCubic, 900ms (compresso)
  useEffect(() => {
    if (phase < 4) return;
    let raf = 0;
    const start = performance.now();
    const DURATION = 900;
    const step = (t) => {
      const p = Math.min(1, (t - start) / DURATION);
      const eased = 1 - Math.pow(1 - p, 3);
      setCounter(Math.round(TARGET_PRICE * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  const showVocale = phase >= 0 && phase <= 1;
  const showCard = phase >= 2;
  const titleVisible = phase >= 3;
  const priceVisible = phase >= 4;
  const portalsVisible = phase >= 5;

  return (
    <div className="max-w-[460px] mx-auto" data-testid="hero-listing-showcase">
      {/* Stage 0 — mode picker: scegli scrivere o parlare */}
      <div
        className="bg-white border border-[#E5E7E0] rounded-xl overflow-hidden text-left transition-all duration-500"
        style={{
          opacity: phase === -1 ? 1 : 0,
          transform: phase === -1 ? "scale(1) translateY(0)" : "scale(0.96) translateY(-6px)",
          maxHeight: phase === -1 ? "200px" : "0px",
          marginBottom: phase === -1 ? "12px" : "0px",
          padding: phase === -1 ? "18px" : "0px",
        }}
        data-testid="hero-mode-picker"
      >
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5A6A4D] mb-2.5">Come vuoi raccontare l&apos;immobile?</div>
        <div className="grid grid-cols-2 gap-2">
          {/* "Scrivo" — non selezionato */}
          <div className="flex items-center gap-2.5 p-2.5 rounded-lg border border-[#E5E7E0] bg-white opacity-55">
            <div className="w-8 h-8 rounded-md bg-[#F1F4ED] flex items-center justify-center flex-shrink-0">
              <PencilSimpleLine size={15} weight="duotone" className="text-[#7C8B6E]" />
            </div>
            <div className="leading-tight">
              <div className="text-[13px] font-semibold text-[#1C1C1A]">Scrivo</div>
              <div className="text-[10px] text-[#9A968B]">compila come al solito</div>
            </div>
          </div>
          {/* "Vocale" — selezionato animato */}
          <div className="flex items-center gap-2.5 p-2.5 rounded-lg border-2 border-[#7C8B6E] bg-[#F1F4ED] shadow-[0_0_0_3px_rgba(124,139,110,0.18)] transition-all">
            <div className="w-8 h-8 rounded-md bg-[#7C8B6E] flex items-center justify-center flex-shrink-0 relative">
              <Microphone size={15} weight="fill" className="text-white" />
              <span className="absolute inset-0 rounded-md bg-[#7C8B6E] animate-ping opacity-30" />
            </div>
            <div className="leading-tight">
              <div className="text-[13px] font-semibold text-[#1C1C1A]">Vocale</div>
              <div className="text-[10px] text-[#5A6A4D]">parla, scrivo io</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stage 1 — vocale sgrammaticato */}
      <div
        className="relative bg-[#0F0F0E] border border-white/10 rounded-xl overflow-hidden text-left transition-all duration-500"
        style={{
          opacity: showVocale ? 1 : 0,
          transform: showVocale ? "scale(1) translateY(0)" : "scale(0.96) translateY(-8px)",
          maxHeight: showVocale ? "340px" : "0px",
          marginBottom: showVocale ? "12px" : "0px",
          padding: showVocale ? "16px" : "0px",
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="relative w-7 h-7 rounded-full bg-[#E63946] flex items-center justify-center shadow">
            <Microphone size={13} weight="fill" className="text-white" />
            {phase === 0 && <span className="absolute inset-0 rounded-full bg-[#E63946] animate-ping opacity-40" />}
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#A8B89A]">
            {phase === 0 ? "Tu parli" : "Pronto sta scrivendo…"}
          </div>
        </div>
        <p className="text-[12px] leading-[1.55] font-mono text-white/85 italic min-h-[140px]">
          &ldquo;{vocaleTyped}
          {phase === 0 && vocaleTyped.length < VOCALE.length && (
            <span className="inline-block w-[6px] h-[12px] bg-[#A8B89A] ml-0.5 align-middle animate-pulse" />
          )}&rdquo;
        </p>
        {phase === 1 && (
          <div className="mt-3 -mx-4 -mb-4 px-4 py-3 bg-gradient-to-r from-amber-500/15 via-amber-500/8 to-transparent border-t border-amber-400/25">
            <div className="flex items-center gap-2 text-[12px] font-semibold text-amber-200">
              <Sparkle size={14} weight="fill" className="text-amber-400 animate-pulse" />
              <span>Trasformo in annuncio per Idealista e Immobiliare…</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="hero-transform-portals">
              {PORTALS.map((p, i) => (
                <span
                  key={p}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-[10px] text-white/85"
                  style={{ animation: `voiceChipIn 0.35s ease-out ${i * 0.18}s both` }}
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Stage 2 — annuncio finito */}
      <div
        className="bg-white border border-[#E5E7E0] rounded-xl overflow-hidden shadow-md text-left transition-all duration-700"
        style={{
          opacity: showCard ? 1 : 0,
          transform: showCard ? "translateY(0)" : "translateY(12px)",
        }}
      >
        <div className="relative">
          <div
            className="aspect-[16/10] bg-cover bg-center transition-all duration-700"
            style={{
              backgroundImage: "url('/landing/sala-original-after.jpg')",
              opacity: showCard ? 1 : 0,
              transform: showCard ? "scale(1)" : "scale(1.04)",
            }}
          />
          <span className="absolute top-2.5 left-2.5 bg-[#1C1C1A] text-white text-[9.5px] uppercase tracking-widest px-2 py-1 rounded font-bold transition-opacity duration-500" style={{ opacity: showCard ? 1 : 0 }}>In vendita</span>
          <span className="absolute top-2.5 right-2.5 bg-white/95 text-[#1C1C1A] text-[10px] px-2 py-1 rounded font-medium transition-opacity duration-500" style={{ opacity: showCard ? 1 : 0 }}>+ 8 foto</span>
        </div>
        <div className="px-4 py-3.5">
          <div className="flex items-baseline justify-between gap-2 mb-1.5 min-h-[24px]">
            <div className="font-editorial text-[16.5px] text-[#1C1C1A] leading-tight">
              {titleVisible && titleTyped}
              {phase === 3 && titleTyped.length < TITLE.length && (
                <span className="inline-block w-[2px] h-[14px] bg-[#7C8B6E] ml-0.5 align-middle animate-pulse" />
              )}
            </div>
            <div
              className="text-[16px] font-bold text-[#7C8B6E] whitespace-nowrap font-mono transition-opacity duration-300"
              style={{ opacity: priceVisible ? 1 : 0 }}
            >
              € {counter.toLocaleString("it-IT")}
            </div>
          </div>
          <div className="text-[10.5px] text-[#6B6A65] mb-1 transition-opacity duration-500" style={{ opacity: titleVisible ? 1 : 0 }}>
            Via Garibaldi · 92 m² · 3 locali · 2° piano
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-[#7C8B6E] mb-2.5 transition-opacity duration-500" style={{ opacity: portalsVisible ? 1 : 0 }}>
            <span className="text-[10px]">📍</span>
            <span><strong className="font-bold">Servizi vicini trovati in automatico:</strong> Fermata bus a 150m · Supermercato a 200m · Parco a 400m</span>
          </div>
          <p className="text-[11.5px] text-[#3F3D38] leading-[1.5] line-clamp-3 transition-opacity duration-500" style={{ opacity: priceVisible ? 1 : 0.2 }}>
            Trilocale luminoso in palazzo signorile, parquet originale restaurato, cucina abitabile, doppi servizi, ascensore e cantina. <em>Ottima esposizione mattino-pomeriggio.</em>
          </p>
          {/* Portal chips */}
          <div className="mt-3 pt-3 border-t border-[#EDEAE2]">
            <div className="text-[8.5px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-1.5">
              Pronto per pubblicare su
            </div>
            <div className="flex flex-wrap gap-1.5 min-h-[26px]">
              {portalsVisible && PORTALS.map((p, i) => (
                <span
                  key={`${loopId}-${p}`}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F1F4ED] border border-[#7C8B6E]/30 text-[10px] text-[#1C1C1A] font-medium"
                  style={{ animation: `voiceChipIn 0.4s ease-out ${i * 0.18}s both` }}
                >
                  <CheckCircle size={10} weight="fill" className="text-[#7C8B6E]" /> {p}
                </span>
              ))}
            </div>
          </div>
          <div className="mt-2.5 flex items-center gap-1 text-[9.5px] uppercase tracking-[0.18em] text-[#9A968B]">
            <Sparkle size={10} weight="fill" className="text-amber-700" /> Generato in 8 sec · 2 portali
          </div>
        </div>
      </div>
      <p className="mt-3 text-[11.5px] sm:text-[12.5px] text-[#6B6A65] italic text-center">
        Da un vocale qualunque. <span className="not-italic text-[#9A968B]">Pronti da pubblicare.</span>
      </p>
    </div>
  );
}

function Header() {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-40 glass-header">
      <div className="max-w-6xl mx-auto px-5 lg:px-10 h-16 sm:h-20 flex items-center justify-between">
        <a href="/" className="relative flex items-center" data-testid="brand-link-landing">
          <img
            src="/logo/full-with-tagline.png?v=2"
            alt="Pronto · per agenti immobiliari"
            className="h-14 sm:h-16 w-auto select-none"
            style={{ borderRadius: "13px" }}
            draggable="false"
          />
          <span
            className="text-[8px] sm:text-[9px] font-bold uppercase tracking-[0.2em] bg-amber-200 text-amber-900 px-1 py-px rounded-sm leading-none shadow-sm self-start mt-1 ml-1"
            data-testid="beta-badge-landing"
          >
            Gratuita
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-8 text-sm text-[#1C1C1A]">
          <a href="#cosa-fa" className="hover:text-[#7C8B6E]" data-testid="nav-features">Cosa fa</a>
          <a href="#comunita" className="hover:text-[#7C8B6E]" data-testid="nav-ideas">Idee</a>
          <a href="#faq" className="hover:text-[#7C8B6E]" data-testid="nav-faq">FAQ</a>
          <a href="/pricing" className="hover:text-[#7C8B6E]" data-testid="nav-pricing">Prezzi</a>
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <button onClick={() => navigate("/dashboard")} className="btn-primary text-xs px-4 py-1.5" data-testid="header-dashboard-btn">
              Dashboard <ArrowRight size={14} weight="bold" />
            </button>
          ) : (
            <button onClick={() => navigate("/login")} className="btn-primary text-sm px-5 py-2" data-testid="header-login-btn">
              Accedi
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const navigate = useNavigate();

  // Tutte le stanze già qui nell'hero — niente scroll verso una gallery duplicata sotto.
  // iter v28 r458 — User M1374: «sposta planimetria al secondo posto dopo
  // arreda». Era in 8a posizione, ora subito dopo "Arreda".
  const rooms = [
    { id: "vuoto",    cat: "restyling", label: "Arreda",        before: "/landing/vuoto-before.jpg", after: "/landing/vuoto-after.jpg",    fallbackBefore: STAGING_EMPTY, fallbackAfter: STAGING_FURNISHED, tagline: "Uno spazio vuoto racconta poco. Arredato, tutto." },
    { id: "pianta",   cat: "restyling", label: "Planimetria",   before: "/landing/pianta-before.jpg?v=27", after: "/landing/pianta-after.jpg?v=27", fallbackBefore: HERO_BEFORE, fallbackAfter: HERO_AFTER,  tagline: "La pianta del catasto, finalmente leggibile." },
    { id: "sala",     cat: "foto",      label: "Living",      before: "/landing/sala-original-before.jpg", after: "/landing/sala-original-after.jpg", fallbackBefore: HERO_BEFORE, fallbackAfter: HERO_AFTER,  tagline: "Come se l'avesse scattata un fotografo." },
    { id: "camera",   cat: "foto",      label: "Camera",      before: "/landing/camera-before.jpg",       after: "/landing/camera-after.jpg",       fallbackBefore: HERO_BEFORE, fallbackAfter: HERO_AFTER,  tagline: "La stessa stanza, con la luce di un servizio professionale." },
    { id: "cucina",   cat: "foto",      label: "Cucina",      before: "/landing/cucina-before.jpg",       after: "/landing/cucina-after.jpg",       fallbackBefore: HERO_BEFORE, fallbackAfter: HERO_AFTER,  tagline: "La cucina di sempre, ma con l'occhio di un professionista." },
    { id: "bagno",    cat: "foto",      label: "Bagno",       before: "/landing/bagno-before.jpg",        after: "/landing/bagno-after.jpg",        fallbackBefore: HERO_BEFORE, fallbackAfter: HERO_AFTER,  tagline: "Bianchi puliti, riflessi giusti. Da editoriale." },
    { id: "esterno",  cat: "foto",      label: "Esterno",     before: "/landing/esterno-before.jpg",      after: "/landing/esterno-after.jpg",      fallbackBefore: HERO_BEFORE, fallbackAfter: HERO_AFTER,  tagline: "Linee dritte, cielo terso. Come una foto da rivista." },
    { id: "rinnova",  cat: "restyling", label: "Restyling",     before: "/landing/rinnova-before.jpg",    after: "/landing/rinnova-after.jpg",      fallbackBefore: HERO_BEFORE, fallbackAfter: HERO_AFTER,  tagline: "Mobili anni '90 → look attuale. Muri intatti." },
    { id: "svuota",   cat: "restyling", label: "Svuota stanza", before: "/landing/svuota-before.jpg?v=iter29b", after: "/landing/svuota-after.jpg?v=iter29b",      fallbackBefore: HERO_BEFORE, fallbackAfter: HERO_AFTER,  tagline: "Arredo del proprietario datato? Svuota e mostra lo spazio reale." },
  ];
  const [active, setActive] = useState("vuoto");
  const [imgError, setImgError] = useState({});
  // Iter 238: nessuna freccia. Su desktop wrap a 2 righe (flex-wrap),
  // su mobile resta scroll orizzontale. Niente più overflow nascosto.
  const stripRef = React.useRef(null);
  const chipRefs = React.useRef({});

  // iter v29 r046 — default "sopralluogo" (era "foto") per coerenza con
  // wedge "Parla, o carica una foto" (sopralluogo = ingresso principale).
  const [showcase, setShowcase] = useState("sopralluogo");
  const [stimaMode, setStimaMode] = useState("inapp"); // "inapp" | "qr"
  const [waMode, setWaMode] = useState("match"); // "match" | "broadcast"

  React.useEffect(() => {
    const node = chipRefs.current[showcase];
    const strip = stripRef.current;
    if (!node || !strip || window.innerWidth >= 1024) return;
    // iter 306 — scroll SOLO orizzontale dello strip, mai del documento.
    // Prima usavamo node.scrollIntoView({block:"nearest"}) che, se la riga
    // delle chip era fuori dal viewport (utente scrollato giù), forzava un
    // jump verticale → la pagina "tornava su" da sola durante la lettura.
    // Soluzione: calcolo manuale del scrollLeft del solo strip.
    const stripRect = strip.getBoundingClientRect();
    const chipRect = node.getBoundingClientRect();
    const targetScrollLeft =
      strip.scrollLeft +
      (chipRect.left - stripRect.left) -
      (stripRect.width / 2 - chipRect.width / 2);
    strip.scrollTo({ left: targetScrollLeft, behavior: "smooth" });
  }, [showcase]);

  // iter v29 r141 — Auto-select primo chip al cambio card (carousel mobile).
  // Quando l'utente swipa orizzontalmente le 3 card-promesse, la card che
  // entra in viewport seleziona automaticamente la sua prima chip così il
  // mockup sotto cambia in tempo reale senza dover toccare nulla.
  const groupFirstChip = {
    "hero-group-g1": "sopralluogo",
    "hero-group-g2": "foto",
    "hero-group-g3": "stima",
  };
  React.useEffect(() => {
    if (window.innerWidth >= 1024) return; // solo mobile/tablet
    const cards = Object.keys(groupFirstChip)
      .map((tid) => document.querySelector(`[data-testid="${tid}"]`))
      .filter(Boolean);
    if (cards.length === 0) return;
    const io = new IntersectionObserver(
      (entries) => {
        // Prendi la card più visibile (maggior intersectionRatio).
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        const tid = visible.target.getAttribute("data-testid");
        const firstChip = groupFirstChip[tid];
        if (firstChip) setShowcase(firstChip);
      },
      { threshold: [0.5, 0.75] },
    );
    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [userTouched, setUserTouched] = useState(0);
  // r354 — userTouched è un counter (non un boolean): ogni interazione lo
  // incrementa, e questo è una dep dell'useEffect di autoplay → ogni
  // interazione resetta il timer di 60s. Con un boolean true→true non si
  // sarebbe attivato il reset.
  const bumpTouched = React.useCallback(() => setUserTouched((c) => c + 1), []);

  // Iter 232: lightweight analytics. Genera un session_id stabile per visita
  // (sessionStorage → dura finché la tab è aperta). Manda fire-and-forget al
  // backend ogni volta che cambia il chip attivo, sia su click utente che
  // su autoplay. Backend distingue "click" vs "autoplay" per il segnale di intent.
  const sessionIdRef = React.useRef(null);
  if (sessionIdRef.current === null) {
    try {
      let sid = window.sessionStorage.getItem("pronto.landing.sid");
      if (!sid) {
        sid = "s_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
        window.sessionStorage.setItem("pronto.landing.sid", sid);
      }
      sessionIdRef.current = sid;
    } catch {
      sessionIdRef.current = "s_" + Math.random().toString(36).slice(2, 10);
    }
  }
  const trackChip = React.useCallback((chip, source) => {
    if (!sessionIdRef.current) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/landing-chip`;
    const payload = JSON.stringify({ chip, source, session_id: sessionIdRef.current });
    // sendBeacon è ideale per fire-and-forget: non blocca il main thread,
    // sopravvive ai navigation events. Fallback su fetch keepalive.
    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: "application/json" });
        navigator.sendBeacon(url, blob);
        return;
      }
    } catch { /* ignore */ }
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  }, []);

  // When the showcase tab changes back to "foto", se l'agente prima aveva
  // chiuso la rotazione, riportiamo la stanza al default "vuoto" (Arreda) —
  // così appena rientra vede la trasformazione più impattante.
  useEffect(() => {
    if (showcase === "foto" && !rooms.find(r => r.id === active)) {
      setActive("vuoto");
    }
  }, [showcase]); // eslint-disable-line react-hooks/exhaustive-deps
  const current = rooms.find((r) => r.id === active) || rooms[0];
  const before = imgError[`${current.id}-before`] && current.fallbackBefore ? current.fallbackBefore : current.before;
  const after  = imgError[`${current.id}-after`]  && current.fallbackAfter  ? current.fallbackAfter  : current.after;

  useEffect(() => {
    [
      { key: `${current.id}-before`, src: current.before },
      { key: `${current.id}-after`,  src: current.after },
    ].forEach(({ key, src }) => {
      if (imgError[key] !== undefined) return;
      const img = new Image();
      img.onload  = () => setImgError((m) => ({ ...m, [key]: false }));
      img.onerror = () => setImgError((m) => ({ ...m, [key]: true  }));
      img.src = src;
    });
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-cycle showcase tabs · 8 valori. Iter 226: l'autoplay si pausa quando
  // l'utente clicca un chip, ma RIPARTE dopo 25s di inattività — prima un click
  // bloccava tutto per sempre, gli utenti vedevano i chip "fermi".
  // Iter r354: utente «se sono dentro Foto e ci interagisco, non far scattare
  // ad altra sezione». Soluzione:
  //   - hover/mousedown/touch sul mockup → pausa per 60s (era 25s)
  //   - se l'utente CONTINUA a interagire (click su tab room, slider drag),
  //     ogni interazione resetta i 60s
  //   - solo dopo 60s di vera inattività riprende il ciclo
  useEffect(() => {
    const order = ["sopralluogo", "foto", "agenda", "annuncio", "vetrina", "social", "auguri", "assistente", "stima", "volantino"];
    const dwell = { foto: 13000, vetrina: 14000, assistente: 16000, sopralluogo: 13000, agenda: 13000, annuncio: 18500, stima: 25000, volantino: 15000, social: 13000, auguri: 25000 };
    const ms = userTouched > 0 ? 60000 : (dwell[showcase] || 12000);
    const id = setTimeout(() => {
      const i = order.indexOf(showcase);
      const next = order[(i === -1 ? 0 : (i + 1) % order.length)];
      setShowcase(next);
      trackChip(next, "autoplay");
      if (userTouched > 0) setUserTouched(0);
    }, ms);
    return () => clearTimeout(id);
  }, [userTouched, showcase, trackChip]);

  return (
    <section className="relative">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-4 sm:pt-8 lg:pt-12 pb-10 sm:pb-16 lg:pb-20 text-center">
        <div className="reveal inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F1F4ED] border border-[#7C8B6E]/25 mb-3 sm:mb-4" data-testid="hero-kicker">
          <span className="w-1.5 h-1.5 rounded-full bg-[#7C8B6E] animate-pulse" />
          <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold text-[#5A6A4D]">
            Per agenti immobiliari · Prova gratuita
          </span>
        </div>
        <h1 className="text-[#1C1C1A]" data-testid="hero-title">
          <span className="block font-editorial text-[32px] sm:text-5xl lg:text-6xl italic-accent text-[#1C1C1A] tracking-tight leading-[1.05]">
            <span className="hero-word-reveal block" style={{ "--w-delay": "0ms" }}>Le case</span>
            <span className="hero-word-reveal block text-[#7C8B6E]" style={{ "--w-delay": "180ms" }}>le vendi tu.</span>
          </span>
          <span
            className="hero-word-reveal block font-editorial italic-accent text-[#B57C5E] text-[20px] sm:text-[28px] lg:text-[32px] tracking-tight leading-[1.1] mt-2 sm:mt-3"
            style={{ "--w-delay": "440ms" }}
            data-testid="hero-title-tagline"
          >
            Al resto ci pensa{" "}
            <span
              className="font-semibold not-italic"
              style={{
                fontFamily: "Montserrat, 'Helvetica Neue', Arial, sans-serif",
                color: "#7C8B6E",
                letterSpacing: "0.005em",
              }}
            >
              Pronto
            </span>
          </span>
        </h1>
        {/* iter v29 r046 — Wedge unico · iter v29 r145 — Claim definitivo
            "Da un vocale e qualche scatto. Tutto pronto in pochi minuti per
            pubblicare e acquisire nuovi incarichi." — dice il COME (voce+foto)
            e il PERCHÉ (pubblicare + acquisire). */}
        <p className="reveal mt-4 sm:mt-6 text-[15px] sm:text-[18px] text-[#1C1C1A] max-w-3xl mx-auto leading-snug px-2" style={{"--reveal-delay":"100ms"}}>
          <span className="block font-medium">
            Da un vocale e qualche scatto.
          </span>
          <span className="block mt-2 text-[13.5px] sm:text-[15px] text-[#6B6A65] font-normal leading-snug">
            <span className="text-[#1C1C1A] font-semibold">Tutto pronto in pochi minuti</span> per pubblicare <span className="text-[#B57C5E] font-semibold">e acquisire nuovi incarichi</span>.
          </span>
          <span className="block mt-3 text-[12px] sm:text-[13px] text-[#9A968D] font-normal italic leading-snug">
            Un'unica app, facile ed intuitiva — quello che oggi fai con cinque programmi, qui lo fai in uno.
          </span>
        </p>

        {/* iter v29 r140 — Tolto flex-col-reverse: non funzionava come voluto
            e creava confusione. Strategia nuova: tap su chip → scroll smooth
            al mockup. Vedi onClick sotto. */}

        {/* iter v29 r140 — Strategia mobile: 3 macro-card in CAROUSEL ORIZZONTALE
            (swipe destra/sinistra) con scroll-snap, così l'utente esplora le
            3 promesse senza scrollare verticalmente e il mockup sotto resta
            sempre visibile nella stessa posizione. Su desktop (lg+) torna
            grid 3 colonne. */}
        <div className="reveal mt-7 sm:mt-9 max-w-5xl mx-auto" style={{"--reveal-delay":"160ms"}}>
          <div className="flex lg:grid lg:grid-cols-3 gap-3 lg:gap-5 overflow-x-auto lg:overflow-visible snap-x snap-mandatory lg:snap-none scroll-px-6 lg:scroll-px-0 -mx-6 lg:mx-0 px-6 lg:px-2 pb-3 lg:pb-0 hide-scroll">
            {[
              {
                key: "g1",
                eyebrow: "01 · Parli, è fatto",
                accent: "#5A6A4D",
                hint: "Vocale ovunque",
                chips: [
                  { id: "sopralluogo", icon: Microphone,    label: "Sopralluogo a voce" },
                  { id: "agenda",      icon: Calendar,      label: "Agenda vocale" },
                  { id: "assistente",  icon: Sparkle,       label: "Assistente" },
                ],
              },
              {
                key: "g2",
                eyebrow: "02 · Materiali, già fatti",
                accent: "#B57C5E",
                hint: "Generati dal sopralluogo",
                chips: [
                  { id: "foto",     icon: Camera,           label: "Foto · restyling · arreda" },
                  { id: "annuncio", icon: PencilSimpleLine, label: "Annuncio" },
                  { id: "social",   icon: ShareNetwork,     label: "Post & reel" },
                  { id: "vetrina",  icon: Storefront,       label: "Vetrina personale" },
                  { id: "auguri",   icon: WhatsappLogo,     label: "Messaggi WhatsApp" },
                ],
              },
              {
                key: "g3",
                eyebrow: "03 · Acquisisci proprietari",
                accent: "#7C8B6E",
                hint: "Trovi nuovi annunci",
                chips: [
                  { id: "stima",     icon: Calculator, label: "Stima" },
                  { id: "volantino", icon: QrCode,     label: "Volantino · stima online" },
                ],
              },
            ].map((g, gi) => (
              <div
                key={g.key}
                className="bg-white rounded-2xl border border-[#EAE7DF] p-4 lg:p-5 transition-all hover:shadow-[0_18px_44px_-22px_rgba(28,28,26,0.16)] hover:-translate-y-0.5 w-[85vw] max-w-[360px] lg:w-auto lg:max-w-none shrink-0 lg:shrink snap-center lg:snap-align-none"
                data-testid={`hero-group-${g.key}`}
                style={{ animationDelay: `${gi * 110}ms` }}
              >
                <div className="flex items-baseline justify-between mb-3 pb-2.5 border-b border-[#EAE7DF]">
                  <span
                    className="text-[10.5px] uppercase tracking-[0.22em] font-bold tabular-nums"
                    style={{ color: g.accent }}
                  >
                    {g.eyebrow}
                  </span>
                  <span className="text-[10px] text-[#9A968B] font-medium tracking-wide hidden sm:inline">
                    {g.hint}
                  </span>
                </div>
                {/* iter v29 r143 — Mobile grid 2x2 (riempie card) · Desktop
                    flex-wrap centrato a pillola (più ariosa, come prima). */}
                <div className="grid grid-cols-2 gap-2 lg:flex lg:flex-wrap lg:justify-center lg:gap-1.5">
                  {g.chips.map(({ id, icon: Icon, label }) => {
                    const isActive = showcase === id;
                    return (
                      <button
                        key={id}
                        ref={(el) => { if (el) chipRefs.current[id] = el; }}
                        type="button"
                        onClick={() => {
                          setShowcase(id);
                          bumpTouched();
                          trackChip(id, "click");
                          // iter v29 r248 — User M1403: «su mobile, cliccando
                          // una chip non si vede l'animazione, devi scendere a
                          // mano». Auto-scroll al mockup area sotto le chip,
                          // SOLO su mobile (<lg) e SOLO su tap utente
                          // (l'auto-cycle non triggera questo path).
                          if (window.innerWidth < 1024) {
                            // Piccolo delay per lasciare il tempo al mockup di
                            // renderizzare il contenuto del nuovo showcase
                            // prima dello scroll (altrimenti calcola un'altezza
                            // sbagliata e finisce a metà strada).
                            setTimeout(() => {
                              const target = document.getElementById("hero-mockup-area");
                              if (target) {
                                target.scrollIntoView({ behavior: "smooth", block: "start" });
                              }
                            }, 120);
                          }
                        }}
                        className={`group relative px-3 py-2.5 lg:pl-2 lg:pr-3 lg:py-1.5 text-[11.5px] sm:text-[12px] font-semibold rounded-xl lg:rounded-full transition-all duration-300 inline-flex items-center justify-center gap-1.5 text-center border lg:whitespace-nowrap ${
                          isActive
                            ? "bg-[#1C1C1A] text-white border-[#1C1C1A] shadow-[0_8px_22px_-8px_rgba(124,139,110,0.55),0_2px_6px_-2px_rgba(28,28,26,0.22)] scale-[1.02]"
                            : "bg-[#FAF8F4] text-[#3F3D38] border-[#EAE7DF] hover:border-current hover:text-[#1C1C1A] hover:-translate-y-0.5"
                        }`}
                        data-testid={`hero-showcase-${id}`}
                        aria-pressed={isActive}
                        aria-label={label}
                      >
                        <Icon size={13} weight={isActive ? "fill" : "regular"} className="shrink-0" />
                        <span className="leading-tight lg:leading-normal">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* iter v29 r140 — Mobile-only hint swipe per carousel orizzontale */}
          <div className="lg:hidden flex items-center justify-center gap-2 mt-3" aria-hidden="true">
            <span className="text-[9.5px] uppercase tracking-[0.22em] font-semibold text-[#9A968D]">
              ← scorri →
            </span>
          </div>

          {/* Progress + numero · tour-style */}
          <div className="flex flex-col items-center gap-2 mt-6 mb-5" aria-hidden="true" data-testid="hero-showcase-dots">
            <div className="flex items-center gap-1.5">
              {["sopralluogo", "agenda", "foto", "annuncio", "vetrina", "social", "auguri", "assistente", "stima", "volantino"].map((id) => (
                <span
                  key={id}
                  className={`rounded-full transition-all duration-300 ${
                    showcase === id
                      ? "w-5 h-1.5 bg-[#7C8B6E]"
                      : "w-1.5 h-1.5 bg-[#C8C5BD]"
                  }`}
                />
              ))}
            </div>
            <span className="text-[9.5px] uppercase tracking-[0.22em] font-semibold text-[#9A968D]">
              {String(["sopralluogo", "agenda", "foto", "annuncio", "vetrina", "social", "auguri", "assistente", "stima", "volantino"].indexOf(showcase) + 1).padStart(2,"0")} / 10 · {({ foto: "Foto da rivista", vetrina: "Vetrina personale", sopralluogo: "Sopralluogo vocale", agenda: "Agenda vocale", annuncio: "Annuncio per i portali", auguri: "Messaggi WhatsApp", stima: "Stima", volantino: "Volantino col QR", social: "Post & Stories", assistente: "Assistente immobiliare" })[showcase]}
            </span>
          </div>
        {/* /reveal mt-7 chip-groups wrapper · close in 478's max-w-3xl scope */}

          {/* "Mandati" chip eliminato iter 240 — il flusso volantino/QR è ora dentro "Stima" (sub-toggle: La fai tu / Volantino QR) */}

          {/* Mossa 2 (marketplace) commentata iter 239 — vedi nota in fondo file */}

          {/* iter v29 r140 — Carousel orizzontale su mobile rende il
              bottone "↑ Altri esempi" inutile (le 3 card sono raggiungibili
              con swipe senza scroll verticale). Tolto. */}
          <div id="hero-mockup-area" className="scroll-mt-4">

          {showcase === "volantino" && (
            <div className="max-w-[300px] sm:max-w-[340px] mx-auto" data-testid="hero-volantino-showcase" onPointerDownCapture={() => bumpTouched()}>
              {/* iter 254: animazione flip 3D — una sola immagine A5 che ruota
                  mostrando alternativamente fronte e retro ogni ~3.5s. Sostituisce
                  la vista a 2 colonne side-by-side che l'utente trovava confusa. */}
              <HeroVolantinoFlip active={showcase === "volantino"} />
              <p className="mt-3 text-[13px] sm:text-[14px] text-[#1C1C1A] font-semibold leading-snug">
                Volantino A5 fronte/retro, col tuo QR per la stima cliente.
              </p>
              <p className="text-[12px] sm:text-[13px] text-[#6B6A65] leading-snug">
                Stampato e lasciato in bacheca, condomini, attività. Il proprietario scansiona, riceve <span className="text-[#7C8B6E] font-semibold">una stima gratuita</span> — a te arriva il contatto.
              </p>
            </div>
          )}

          {showcase === "assistente" && (
            <div className="max-w-[480px] mx-auto" data-testid="hero-assistant-showcase" onPointerDownCapture={() => bumpTouched()}>
              <HeroAssistantMockup />
              <p className="mt-3 text-[13px] sm:text-[14px] text-[#1C1C1A] font-semibold leading-snug">
                Un consulente immobiliare nella tua tasca.
              </p>
              <p className="text-[12px] sm:text-[13px] text-[#6B6A65] leading-snug">
                Cedolare, IMU, contratti, riscaldamento centralizzato, codice civile, come usare l'app — chiedi e Pronto risponde. <span className="text-[#7C8B6E] font-semibold">Mai più dubbi in trattativa.</span>
              </p>
            </div>
          )}

          {showcase === "foto" && (
            <div
              onPointerDownCapture={bumpTouched}
              onMouseMove={bumpTouched}
              onMouseLeave={() => {
                // Quando esci, lascia il timer 60s a scorrere
              }}
            >
              <div
                className="max-w-[620px] lg:max-w-[760px] mx-auto"
                data-testid="hero-before-after"
              >
                <div className="flex items-center justify-center gap-2 mb-3 flex-wrap">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F1F4ED] border border-[#7C8B6E]/25">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#7C8B6E]"/>
                    <span className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-[#5A6A4D]">Oggi → Domani · stessa stanza</span>
                  </div>
                </div>
                <BeforeAfterSlider
                  key={current.id}
                  beforeSrc={before}
                  afterSrc={after}
                  height="h-[280px] sm:h-[400px] lg:h-[500px]"
                  autoPlay
                />
                <p className="mt-2.5 text-[13px] sm:text-[14px] text-[#1C1C1A] font-semibold leading-snug">
                  {current.tagline}
                </p>
                <p className="text-[12px] sm:text-[13px] text-[#6B6A65] leading-snug">
                  Carichi 1 foto · Pronto sistema luce, colori, ordine in pochi secondi.
                </p>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5 sm:gap-2 justify-center" role="tablist" data-testid="hero-room-tabs">
                {rooms.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => { setActive(r.id); bumpTouched(); }}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 text-[12px] sm:text-[13px] font-medium rounded-full transition-all ${
                      active === r.id
                        ? "bg-[#7C8B6E] text-white"
                        : "bg-white border border-[#E5E7E0] text-[#1C1C1A] hover:border-[#7C8B6E]"
                    }`}
                    data-testid={`hero-room-${r.id}`}
                    aria-pressed={active === r.id}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {showcase === "vetrina" && (
            <div className="max-w-[520px] mx-auto" data-testid="hero-vetrina-showcase" onPointerDownCapture={() => bumpTouched()}>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F1F4ED] border border-[#7C8B6E]/25 mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#7C8B6E] animate-pulse"/>
                <span className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-[#5A6A4D]">Novità · vetrina personale col tuo marchio</span>
              </div>
              <HeroVetrinaMockup />
              <p className="mt-4 text-[13px] sm:text-[14px] text-[#1C1C1A] font-semibold leading-snug">
                Una <span className="text-[#7C8B6E]">vetrina personale per ogni immobile</span> — col tuo brand, i tuoi colori, il tuo carattere.
              </p>
              <p className="text-[12px] sm:text-[13px] text-[#6B6A65] leading-snug">
                <span className="text-[#7C8B6E] font-semibold">15 palette</span> studiate per il settore e <span className="text-[#7C8B6E] font-semibold">4 layout editoriali</span> (Elegante, Moderno, Minimal, Lussuoso). Tocca e cambia tutto in un attimo — la condividi al cliente con un link e fa la sua figura ovunque.
              </p>
            </div>
          )}

          {showcase === "agenda" && (
            <div className="max-w-[480px] mx-auto" data-testid="hero-agenda-showcase" onPointerDownCapture={() => bumpTouched()}>
              <HeroAgendaMockup />
              <p className="mt-3 text-[13px] sm:text-[14px] text-[#1C1C1A] font-semibold leading-snug">
                Parli mentre guidi. L'agenda si scrive da sola.
              </p>
              <p className="text-[12px] sm:text-[13px] text-[#6B6A65] leading-snug">
                <span className="italic">"Domani alle 15, visita in Brera con i Bianchi"</span> — lo dici a voce. <span className="text-[#7C8B6E] font-semibold">Appuntamento, contatto, promemoria al cliente</span> — fatti. Niente post-it, niente form.
              </p>
            </div>
          )}

          {showcase === "sopralluogo" && (
            <div className="max-w-[480px] mx-auto" data-testid="hero-sopralluogo-showcase" onPointerDownCapture={() => bumpTouched()}>
              <HeroSopralluogoMockup />
              <p className="mt-3 text-[13px] sm:text-[14px] text-[#1C1C1A] font-semibold leading-snug">
                Parli e scatti insieme. Mai più appunti su carta.
              </p>
              <p className="text-[12px] sm:text-[13px] text-[#6B6A65] leading-snug">
                Foto durante il vocale, <span className="text-[#7C8B6E] font-semibold">pausa quando vuoi</span>. Torni in macchina e la scheda è già pronta: descrizione, foto sistemate, dati compilati.
              </p>
            </div>
          )}

          {showcase === "social" && (
            <div className="max-w-[480px] mx-auto" data-testid="hero-social-showcase" onPointerDownCapture={() => bumpTouched()}>
              <HeroSocialMockup />
              {/* iter v28 r243 — User M1161: rimosso SocialPostsStrip
                  (4 thumb piccoli sotto) — basta il mockup principale
                  che già cicla 6 contenuti reali. */}
              <p className="mt-4 text-[13px] sm:text-[14px] text-[#1C1C1A] font-semibold leading-snug">
                I post dei tuoi annunci già fatti, da pubblicare in 1 tocco.
              </p>
              <p className="text-[12px] sm:text-[13px] text-[#6B6A65] leading-snug">
                Storie, caroselli e reel impaginati con foto + testo + il tuo logo. <span className="text-[#7C8B6E] font-semibold">Pubblichi su Instagram, Facebook, WhatsApp e TikTok</span> direttamente da Pronto.
              </p>
              <p className="text-[12px] sm:text-[13px] text-[#6B6A65] leading-snug mt-2">
                Caption inclusa: scheda immobile completa nello stile del post (Moderno, Elegante, Minimal o Lussuoso), senza emoji.
              </p>
            </div>
          )}

          {showcase === "auguri" && (
            <div className="max-w-[480px] mx-auto" data-testid="hero-auguri-showcase" onPointerDownCapture={() => bumpTouched()}>
              {/* Sub-toggle: match con i contatti oppure broadcast/auguri */}
              <div className="flex justify-center gap-1.5 mb-4 flex-wrap" role="tablist" aria-label="Modalità messaggi WhatsApp">
                <button
                  type="button"
                  onClick={() => setWaMode("match")}
                  className={`px-3 py-1.5 text-[11.5px] font-medium rounded-full border transition-all ${
                    waMode === "match"
                      ? "bg-[#1C1C1A] text-white border-[#1C1C1A] shadow-sm"
                      : "bg-white text-[#3F3D38] border-[#EAE7DF] hover:border-[#7C8B6E]"
                  }`}
                  data-testid="hero-wa-mode-match"
                  aria-pressed={waMode === "match"}
                >
                  Match con i contatti
                </button>
                <button
                  type="button"
                  onClick={() => setWaMode("broadcast")}
                  className={`px-3 py-1.5 text-[11.5px] font-medium rounded-full border transition-all ${
                    waMode === "broadcast"
                      ? "bg-[#1C1C1A] text-white border-[#1C1C1A] shadow-sm"
                      : "bg-white text-[#3F3D38] border-[#EAE7DF] hover:border-[#7C8B6E]"
                  }`}
                  data-testid="hero-wa-mode-broadcast"
                  aria-pressed={waMode === "broadcast"}
                >
                  Auguri & promo
                </button>
              </div>

              {waMode === "match" ? <ContactsMatchMockup /> : <HeroMessaggiMockup />}

              {waMode === "match" ? (
                <>
                  <p className="mt-3 text-[13px] sm:text-[14px] text-[#1C1C1A] font-semibold leading-snug">
                    Carichi un immobile · Pronto cerca chi lo stava già aspettando.
                  </p>
                  <p className="text-[12px] sm:text-[13px] text-[#6B6A65] leading-snug">
                    Pronto incrocia il nuovo immobile con le richieste salvate nei tuoi contatti: <span className="text-[#7C8B6E] font-semibold">"3 persone cercavano un trilocale in Brera a ≤ 400k"</span>. Apre WhatsApp con il messaggio già scritto, uno per uno. Tu controlli e invii.
                  </p>
                </>
              ) : (
                <>
                  <p className="mt-3 text-[13px] sm:text-[14px] text-[#1C1C1A] font-semibold leading-snug">
                    Lo <span className="text-[#7C8B6E]">stesso messaggio</span> a tutti, ma scritto per ognuno.
                  </p>
                  <p className="text-[12px] sm:text-[13px] text-[#6B6A65] leading-snug">
                    Scrivi una sola volta gli auguri di Natale, Pasqua, una promo, una novità. Pronto lo manda a <span className="text-[#7C8B6E] font-semibold">tutti i tuoi contatti insieme</span> ma <span className="text-[#1C1C1A] font-semibold">cambia il nome, la città, l'ultimo immobile visto</span> per ciascuno. Apre WhatsApp pronto da inviare, uno per uno. <span className="italic text-[#5A6A4D]">Niente broadcast, niente spam, niente "Caro cliente".</span>
                  </p>
                </>
              )}
            </div>
          )}

          {showcase === "contatti" && (
            <div className="max-w-[480px] mx-auto" data-testid="hero-contatti-showcase" onPointerDownCapture={() => bumpTouched()}>
              <HeroContattiMockup />
              <p className="mt-3 text-[13px] sm:text-[14px] text-[#1C1C1A] font-semibold leading-snug">
                Pubblichi una casa e Pronto trova chi la stava già cercando.
              </p>
              <p className="text-[12px] sm:text-[13px] text-[#6B6A65] leading-snug">
                Cerca nella tua rubrica e nei tuoi vecchi clienti: <span className="text-[#7C8B6E] font-semibold">"3 persone cercavano un bilocale a Brera 600-700k"</span> → li chiami prima di mettere l'annuncio sui portali.
              </p>
            </div>
          )}

          {showcase === "annuncio" && (
            <div className="max-w-[480px] mx-auto" onPointerDownCapture={() => bumpTouched()}>
              <ListingShowcase />
              <p className="mt-3 text-[13px] sm:text-[14px] text-[#1C1C1A] font-semibold leading-snug">
                Parli al telefono. L'annuncio è già pronto per Idealista e Immobiliare.
              </p>
              <p className="text-[12px] sm:text-[13px] text-[#6B6A65] leading-snug">
                Niente più ore davanti al PC a ricopiare lo stesso testo su <span className="text-[#7C8B6E] font-semibold">Idealista e Immobiliare.it</span>. Pronto adatta lunghezze, parole-chiave e tono per ciascuno.
              </p>
            </div>
          )}

          {showcase === "messaggi" && (
            <div className="max-w-[480px] mx-auto" data-testid="hero-messaggi-showcase" onPointerDownCapture={() => bumpTouched()}>
              <HeroMessaggiMockup />
              <p className="mt-3 text-[13px] sm:text-[14px] text-[#1C1C1A] font-semibold leading-snug">
                Scrivi UN messaggio. Pronto lo manda a tutta la tua rubrica.
              </p>
              <p className="text-[12px] sm:text-[13px] text-[#6B6A65] leading-snug">
                Auguri Natale, riapertura agenzia, anniversario d'acquisto. <span className="text-[#7C8B6E] font-semibold">Cambia nome per ognuno</span>, parte dal tuo WhatsApp, sembra scritto da te uno per uno.
              </p>
            </div>
          )}

          {showcase === "stima" && (
            <div className="max-w-[480px] mx-auto" data-testid="hero-stima-showcase" onPointerDownCapture={() => bumpTouched()}>
              {/* Sub-toggle: la fai tu in app oppure mandi il volantino col QR al cliente */}
              <div className="flex justify-center gap-1.5 mb-4" role="tablist" aria-label="Modalità stima">
                <button
                  type="button"
                  onClick={() => setStimaMode("inapp")}
                  className={`px-3 py-1.5 text-[11.5px] font-medium rounded-full border transition-all ${
                    stimaMode === "inapp"
                      ? "bg-[#1C1C1A] text-white border-[#1C1C1A] shadow-sm"
                      : "bg-white text-[#3F3D38] border-[#EAE7DF] hover:border-[#7C8B6E]"
                  }`}
                  data-testid="hero-stima-mode-inapp"
                  aria-pressed={stimaMode === "inapp"}
                >
                  La fai tu
                </button>
                <button
                  type="button"
                  onClick={() => setStimaMode("qr")}
                  className={`px-3 py-1.5 text-[11.5px] font-medium rounded-full border transition-all ${
                    stimaMode === "qr"
                      ? "bg-[#1C1C1A] text-white border-[#1C1C1A] shadow-sm"
                      : "bg-white text-[#3F3D38] border-[#EAE7DF] hover:border-[#7C8B6E]"
                  }`}
                  data-testid="hero-stima-mode-qr"
                  aria-pressed={stimaMode === "qr"}
                >
                  Volantino · stima online
                </button>
              </div>

              {stimaMode === "inapp" ? <HeroStimaMockup /> : <HeroMandatiMockup />}

              {stimaMode === "inapp" ? (
                <>
                  <p className="mt-3 text-[13px] sm:text-[14px] text-[#1C1C1A] font-semibold leading-snug">
                    Scrivi indirizzo e metri quadri. In 30 secondi sai quanto vale.
                  </p>
                  <p className="text-[12px] sm:text-[13px] text-[#6B6A65] leading-snug">
                    Range di prezzo basato su <span className="text-[#7C8B6E] font-semibold">dati ufficiali dell&apos;Agenzia delle Entrate</span> + annunci comparabili nella stessa zona. Stima approssimativa.
                  </p>
                </>
              ) : (
                <>
                  <p className="mt-3 text-[13px] sm:text-[14px] text-[#1C1C1A] font-semibold leading-snug">
                    Volantino col Tuo QR · <span className="text-[#7C8B6E]">stampato in agenzia o inviato online</span>.
                  </p>
                  <p className="text-[12px] sm:text-[13px] text-[#6B6A65] leading-snug">
                    Il proprietario inquadra il QR (o apre il link) e fa la stima della sua casa in 30 secondi. <span className="text-[#7C8B6E] font-semibold">Tu ricevi il contatto già qualificato</span> — strumento di acquisizione, non solo materiale stampato.
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {/* iter v29 r046 — Rimossa punchline "Le case le vendi tu" duplicata
            (ora è in cima come tagline). Sotto: mini-griglia "ingresso → output"
            per ricordare il wedge prima della CTA. */}
        </div>{/* /hero-mockup-area · v29 r140 */}
        <div className="reveal mt-7 sm:mt-9 max-w-md mx-auto" style={{"--reveal-delay":"180ms"}} data-testid="hero-wedge-recap">
          <div className="text-center text-[10.5px] sm:text-[11px] uppercase tracking-[0.24em] font-semibold text-[#7C8B6E] mb-3">
            Da una foto · o da un vocale
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              { label: "Foto migliorate", sub: "luce, ordine, fedeli" },
              { label: "Annuncio", sub: "completo, pronto" },
              { label: "Vetrina", sub: "col tuo brand" },
              { label: "Post social", sub: "Instagram, FB" },
              { label: "Messaggi", sub: "WhatsApp ai clienti" },
            ].map((o, i) => (
              <div
                key={o.label}
                className={`bg-white border border-[#EAE7DF] rounded-lg px-3 py-2 text-center ${
                  i === 4 ? "col-span-2 sm:col-span-1" : ""
                }`}
              >
                <div className="text-[13px] sm:text-[14px] font-semibold text-[#1C1C1A] leading-tight">{o.label}</div>
                <div className="text-[10.5px] text-[#7C8B6E] mt-0.5">{o.sub}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-center text-[12px] text-[#6B6A65] italic">
            Senza scrivere una riga.
          </div>
        </div>

        <div className="reveal mt-6 sm:mt-7 flex flex-col sm:flex-row items-center justify-center gap-3" style={{"--reveal-delay":"220ms"}}>
          <button onClick={() => navigate("/register")} className="btn-primary" data-testid="hero-cta-primary">
            Iscrizione gratuita  <ArrowRight size={18} weight="bold" />
          </button>
          <button
            onClick={() => navigate("/prova")}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white border border-[#7C8B6E] text-[#7C8B6E] hover:bg-[#F1F4ED] text-[14px] font-medium transition-colors"
            data-testid="hero-cta-secondary"
          >
            Provala senza registrarti
          </button>
        </div>

        <p className="reveal mt-4 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-[#6B6A65]" style={{"--reveal-delay":"280ms"}} data-testid="hero-trial-line">
          <Sparkle size={10} weight="fill" className="inline -mt-0.5 mr-1 text-amber-700" />
          Gratis · senza carta · pronto in 1 minuto
        </p>

        <div className="reveal mt-4 flex items-center justify-center gap-3 sm:gap-5 flex-wrap text-[11px] text-[#6B6A65]" style={{"--reveal-delay":"320ms"}} data-testid="hero-trust-strip">
          <span className="inline-flex items-center gap-1.5"><Lock size={12} weight="regular" /> Dati criptati</span>
          <span className="w-px h-3 bg-[#E5E7E0] hidden sm:inline-block"></span>
          <span className="inline-flex items-center gap-1.5"><Flag size={12} weight="regular" /> Server EU · GDPR</span>
          <span className="w-px h-3 bg-[#E5E7E0] hidden sm:inline-block"></span>
          <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} weight="regular" /> Privacy e sicurezza</span>
        </div>

        {/* Social proof — solo "active agents" reali (= hanno creato ≥1 immobile).
            Auto-nascosto se 0 o non disponibile. Niente cazzate. */}
        <SocialProofStrip />

        <a
          href="#cosa-fa"
          className="sm:hidden reveal mt-6 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[#7C8B6E] animate-bounce-slow"
          style={{"--reveal-delay":"360ms"}}
          data-testid="hero-scroll-hint"
        >
          Scopri di più
          <ArrowRight size={12} weight="bold" className="rotate-90" />
        </a>
      </div>
    </section>
  );
}

function SocialProofStrip() {
  const [count, setCount] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/public/stats`)
      .then((r) => r.json())
      .then((d) => {
        // Mostra SOLO se active_agents > 0 — niente cazzate da signup vuoti
        if (!cancelled && d && Number(d.active_agents) > 0) {
          setCount(Number(d.active_agents));
        }
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);
  if (!count) return null;   // auto-hide se 0 o non caricato
  return (
    <div
      className="reveal mt-6 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-[#E5E7E0] text-[#3A3A36]"
      style={{"--reveal-delay":"360ms"}}
      data-testid="hero-social-proof"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
      <span className="text-[11.5px] sm:text-[12px]">
        <strong className="font-semibold text-[#1C1C1A]">{count}</strong> agenti hanno già usato Pronto
      </span>
    </div>
  );
}

const API = `${BACKEND_URL}/api`;

/**
 * LiveTryItCard — l'utente trascina una foto e VEDE la trasformazione AI in 10 secondi,
 * senza registrarsi. Endpoint: POST /api/public/demo/enhance (3 prove al giorno per IP, watermark applicato).
 *
 * È il momento "wow" che fa capire in 1 colpo cosa fa Pronto. Dopo il successo, CTA forte
 * verso /register per fare lo stesso con tutte le foto.
 */
function LiveTryItCard() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [stage, setStage] = useState("idle"); // idle | loading | done | error
  const [originalPreview, setOriginalPreview] = useState(null);
  const [enhancedUrl, setEnhancedUrl] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [progress, setProgress] = useState(0);
  const [quota, setQuota] = useState(null); // {used, remaining, limit}

  // iter v28 r313 — Bypass quota per email whitelist.
  // Se l'utente arriva con ?email=baigipp@gmail.com lo salviamo in
  // localStorage e lo passiamo a /quota + /enhance per saltare il
  // rate limit (3 prove/IP). Stessa logica di /demo.
  const [emailKey, setEmailKey] = useState(() => {
    try { return localStorage.getItem("vista_demo_email") || ""; } catch { return ""; }
  });
  useEffect(() => {
    const e = new URL(window.location.href).searchParams.get("email");
    if (e) {
      try { localStorage.setItem("vista_demo_email", e); } catch { /* noop */ }
      setEmailKey(e);
    }
  }, []);

  // Fetch quota at mount + after each enhance
  useEffect(() => {
    let alive = true;
    const params = emailKey ? { email: emailKey } : {};
    axios.get(`${API}/public/demo/quota`, { params }).then(({ data }) => {
      if (!alive) return;
      setQuota({ used: data.used, remaining: data.remaining, limit: data.limit });
    }).catch(() => {});
    return () => { alive = false; };
  }, [emailKey]);

  const onPick = () => fileInputRef.current?.click();

  // iter v28 r316 — salvato il File originale per "Rigenera"
  const [lastFile, setLastFile] = useState(null);

  const runEnhance = async (f) => {
    setStage("loading");
    setProgress(0);
    setEnhancedUrl(null);
    setErrorMsg("");

    // iter v29 r062 — Curva su 6 step (analisi → luce → linee → colori
    // → riordino → tocco) tarata su 30-40s reali. Ogni step attivo 5-8s.
    const startedAt = Date.now();
    const progTimer = setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      setProgress((p) => {
        let target;
        if (elapsed < 4)        target = elapsed * 3.0;             // 0→12%
        else if (elapsed < 10)  target = 12 + (elapsed - 4) * 2.6;  // 12→28%
        else if (elapsed < 18)  target = 28 + (elapsed - 10) * 2.2; // 28→46%
        else if (elapsed < 26)  target = 46 + (elapsed - 18) * 2.0; // 46→62%
        else if (elapsed < 34)  target = 62 + (elapsed - 26) * 2.5; // 62→82%
        else                    target = 82 + Math.min(10, (elapsed - 34) * 0.8); // 82→92%
        return Math.min(Math.max(p, target), 92);
      });
    }, 350);

    try {
      const fd = new FormData();
      fd.append("file", f);
      const url = emailKey
        ? `${API}/public/demo/enhance?email=${encodeURIComponent(emailKey)}`
        : `${API}/public/demo/enhance`;
      const { data } = await axios.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90_000,
      });
      clearInterval(progTimer);
      setProgress(100);
      setEnhancedUrl(`data:${data.mime};base64,${data.enhanced_b64}`);
      if (typeof data.remaining === "number") {
        setQuota((q) => q ? { ...q, used: q.limit - data.remaining, remaining: data.remaining } : { used: 1, remaining: data.remaining, limit: data.remaining + 1 });
      }
      setStage("done");
    } catch (err) {
      clearInterval(progTimer);
      const status = err?.response?.status;
      // iter v29 r062 — Errori più chiari sulla landing. L'utente
      // vedeva sempre "Qualcosa non ha funzionato" senza capire se era
      // quota, timeout di Gemini, o key esaurita su Universal Key.
      let msg;
      if (status === 429) {
        msg = "Hai usato tutte e 3 le prove gratuite di oggi. Torna domani — o registrati per usarla senza limiti.";
        setQuota((q) => q ? { ...q, remaining: 0, used: q.limit } : null);
      } else if (status === 402 || status === 503) {
        msg = "Il servizio di elaborazione foto è momentaneamente sovraccarico. Riprova fra 1-2 minuti.";
      } else if (status === 408 || err?.code === "ECONNABORTED") {
        msg = "Ci sta mettendo più del previsto. Prova con una foto più piccola, o riprova fra qualche secondo.";
      } else if (status >= 500) {
        msg = "Qualcosa non ha funzionato di nostro. Riprova fra qualche secondo — se persiste scrivici a supporto@prontoimmo.it.";
      } else {
        msg = err?.response?.data?.detail || "Connessione interrotta. Controlla la rete e riprova.";
      }
      setErrorMsg(msg);
      setStage("error");
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Foto troppo grande (max 10 MB)");
      return;
    }
    setLastFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setOriginalPreview(ev.target.result);
    reader.readAsDataURL(f);
    await runEnhance(f);
  };

  const onRegenerate = () => { if (lastFile) runEnhance(lastFile); };

  const onReset = () => {
    setStage("idle");
    setOriginalPreview(null);
    setEnhancedUrl(null);
    setErrorMsg("");
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <section className="bg-[#FAFAF7] border-y border-[#EDEAE2] py-12 sm:py-16" data-testid="live-try-section">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-amber-300/50 mb-4">
          <Sparkle size={12} weight="fill" className="text-amber-600" />
          <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold text-amber-800">
            Provala adesso · senza registrarti
          </span>
        </div>
        <h3 className="font-editorial text-[24px] sm:text-3xl lg:text-[36px] tracking-tight leading-[1.15] text-[#1C1C1A]">
          Carica <span className="italic-accent text-[#7C8B6E]">una tua foto</span>.<br className="sm:hidden" />
          {" "}E guarda cosa succede.
        </h3>
        <p className="mt-3 text-[14px] sm:text-[15px] text-[#6B6A65] max-w-lg mx-auto leading-snug">
          Una foto vera del tuo prossimo sopralluogo. La luce, l'ordine, le linee storte.
          Pronto la sistema in pochi secondi. <span className="text-[#3F3D38]">Zero registrazione.</span>
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFile}
          data-testid="live-try-file-input"
        />

        {/* IDLE — drop zone */}
        {stage === "idle" && (
          <>
            <button
              onClick={quota?.remaining === 0 ? () => navigate("/register") : onPick}
              className={`mt-7 group w-full max-w-xl mx-auto block bg-white border-2 border-dashed rounded-xl p-8 sm:p-10 transition-all ${
                quota?.remaining === 0
                  ? "border-amber-400/60 cursor-pointer hover:bg-amber-50/40"
                  : "border-[#7C8B6E]/40 hover:border-[#7C8B6E] hover:bg-[#F1F4ED]/40"
              }`}
              data-testid="live-try-drop-zone"
            >
              <div className={`w-14 h-14 mx-auto rounded-full text-white flex items-center justify-center mb-3 group-hover:scale-110 transition-transform ${quota?.remaining === 0 ? "bg-amber-600" : "bg-[#7C8B6E]"}`}>
                <UploadSimple size={26} weight="bold" />
              </div>
              <div className="font-editorial text-[18px] sm:text-xl text-[#1C1C1A] mb-1">
                {quota?.remaining === 0 ? "Hai finito le prove di oggi" : "Carica una foto"}
              </div>
              <div className="text-[12px] sm:text-[13px] text-[#6B6A65]">
                {quota?.remaining === 0
                  ? "Torna domani · oppure registrati per usarla senza limiti"
                  : "JPG o PNG · max 10 MB"}
              </div>
            </button>
            <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-[#E5E7E0] text-[11px] text-[#6B6A65]" data-testid="live-try-quota">
              <span className={`w-1.5 h-1.5 rounded-full ${quota?.remaining === 0 ? "bg-amber-500" : quota?.remaining === 1 ? "bg-amber-500" : "bg-[#7C8B6E]"}`} />
              {quota
                ? quota.remaining === 0
                  ? <span className="font-medium">0 prove rimaste oggi</span>
                  : <span><span className="font-semibold text-[#1C1C1A]">{quota.remaining}</span> {quota.remaining === 1 ? "prova rimasta" : "prove rimaste"} oggi <span className="text-[#9A968B]">· su {quota.limit}</span></span>
                : <span>3 prove gratuite al giorno</span>
              }
            </div>
          </>
        )}

        {/* LOADING — progress multi-step con la foto dell'utente
            iter v29 r060 — Il modello reale impiega 15-40s. Mostriamo 5 step
            che si attivano progressivamente (Analisi → Bilancio luce →
            Raddrizzo linee → Pulizia colori → Ultimo tocco) così l'attesa
            è meno percepita. */}
        {stage === "loading" && (
          <div className="mt-7 max-w-xl mx-auto" data-testid="live-try-loading">
            <div className="relative rounded-xl overflow-hidden border border-[#E5E7E0] aspect-[4/3] bg-[#F1F4ED]">
              {originalPreview && (
                <img src={originalPreview} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "saturate(0.5) brightness(0.85)" }} />
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-[#1C1C1A]/45 via-[#1C1C1A]/20 to-[#1C1C1A]/55 flex items-center justify-center p-5">
                <div className="w-full max-w-[300px] bg-white rounded-2xl px-5 py-4 shadow-xl border border-[#E5E7E0]">
                  <div className="font-editorial text-[15px] text-[#1C1C1A] text-center" data-testid="live-try-loading-title">
                    Pronto sta lavorando…
                  </div>
                  {/* Lista step animata. r062: aggiunto "Riordino" come step
                      finale prima del tocco — l'utente vede chiaramente che
                      Pronto fa anche pulizia visiva, non solo correzione luce. */}
                  <ul className="mt-3 space-y-1.5">
                    {[
                      { pct: 10, label: "Analisi della foto" },
                      { pct: 26, label: "Bilancio della luce" },
                      { pct: 44, label: "Raddrizzo le linee" },
                      { pct: 62, label: "Pulizia dei colori" },
                      { pct: 80, label: "Riordino oggetti" },
                      { pct: 92, label: "Ultimo tocco" },
                    ].map((step, i) => {
                      const thresholds = [10,26,44,62,80,92];
                      const done = progress > step.pct + 6;
                      const active = !done && progress >= (i === 0 ? 0 : thresholds[i-1] - 4);
                      return (
                        <li key={i} className="flex items-center gap-2.5 text-[12.5px]" data-testid={`live-try-step-${i}`}>
                          <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                            done ? "bg-[#7C8B6E] text-white"
                            : active ? "bg-[#FAF8F4] border-2 border-[#7C8B6E]"
                            : "bg-[#EDEAE2] border border-[#D8D6CE]"
                          }`}>
                            {done ? (
                              <svg viewBox="0 0 20 20" className="w-3 h-3" fill="currentColor"><path d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4L8.5 12l6.8-6.7a1 1 0 0 1 1.4 0z"/></svg>
                            ) : active ? (
                              <span className="w-1.5 h-1.5 rounded-full bg-[#7C8B6E] animate-pulse" />
                            ) : null}
                          </span>
                          <span className={`transition-colors ${
                            done ? "text-[#6B6A65] line-through decoration-[#C8CFC0]"
                            : active ? "text-[#1C1C1A] font-semibold"
                            : "text-[#9A968B]"
                          }`}>
                            {step.label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {/* Progress bar */}
                  <div className="mt-3 w-full h-1.5 bg-[#EDEAE2] rounded-full overflow-hidden">
                    <div className="h-full bg-[#7C8B6E] transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
                  </div>
                  {/* iter v29 r062 — Messaggio dinamico invece di "92%" fermo:
                      quando si arriva al limite massimo (92%) il numero
                      sembra rotto. Mostriamo un sottotitolo di rassicurazione
                      che cambia in base alla fase. */}
                  <div className="mt-2 text-[10.5px] uppercase tracking-[0.16em] text-[#9A968B] text-center">
                    {progress < 14  && "Sto guardando bene"}
                    {progress >= 14 && progress < 30 && "Apro le ombre giuste"}
                    {progress >= 30 && progress < 48 && "Sistemo le linee storte"}
                    {progress >= 48 && progress < 66 && "Pulisco i toni"}
                    {progress >= 66 && progress < 84 && "Riordino la scena"}
                    {progress >= 84 && progress < 92 && "Quasi pronto"}
                    {progress >= 92 && "Ancora pochi secondi…"}
                  </div>
                </div>
              </div>
            </div>
            <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-[#6B6A65] text-center">
              <Sparkle size={11} weight="fill" className="inline -mt-0.5 mr-1 text-amber-700" />
              fino a 40 secondi · resta in pagina, vale l'attesa
            </p>
          </div>
        )}

        {/* DONE — before/after slider with their actual photo */}
        {stage === "done" && originalPreview && enhancedUrl && (
          <div className="mt-7" data-testid="live-try-result">
            <div className="max-w-2xl mx-auto">
              <BeforeAfterSlider
                beforeSrc={originalPreview}
                afterSrc={enhancedUrl}
                height="h-[320px] sm:h-[440px]"
              />
            </div>
            <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate("/register")}
                className="btn-primary"
                data-testid="live-try-cta-register"
              >
                Voglio farlo con tutte le mie foto <ArrowRight size={18} weight="bold" />
              </button>
              <button
                onClick={onRegenerate}
                disabled={!lastFile}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium text-[#7C8B6E] border border-[#7C8B6E]/40 rounded-full hover:bg-[#7C8B6E]/10 disabled:opacity-50 transition-colors"
                data-testid="live-try-regenerate"
                title="Non ti piace il risultato? Pronto riprova sulla stessa foto."
              >
                <Sparkle size={14} weight="fill" /> Rigenera
              </button>
              <button
                onClick={onReset}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-[13px] text-[#6B6A65] hover:text-[#1C1C1A] transition-colors"
                data-testid="live-try-reset"
              >
                <UploadSimple size={14} /> Prova un'altra foto
              </button>
            </div>
            <p className="mt-3 text-[11px] text-[#6B6A65] italic">
              La foto resta tua. Pronto non la conserva.
            </p>
          </div>
        )}

        {/* ERROR */}
        {stage === "error" && (
          <div className="mt-7 max-w-md mx-auto bg-white border border-amber-300/60 rounded-xl p-5 text-left" data-testid="live-try-error">
            <div className="font-editorial text-[15px] text-[#1C1C1A] mb-1">Ops.</div>
            <p className="text-[13px] text-[#6B6A65] mb-3">{errorMsg}</p>
            <button
              onClick={onReset}
              className="text-[12px] uppercase tracking-[0.18em] text-[#7C8B6E] hover:underline"
              data-testid="live-try-retry"
            >
              Riprova →
            </button>
          </div>
        )}
      </div>
    </section>
  );
}


// eslint-disable-next-line no-unused-vars
function PrivacyStrip() {
  return (
    <section className="bg-[#FAFAF7] border-y border-[#EDEAE2] py-10 sm:py-12" data-testid="privacy-strip">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-7 sm:mb-8">
          <span className="overline">Privacy e sicurezza</span>
          <h3 className="font-editorial text-[22px] sm:text-2xl lg:text-[28px] mt-2.5 leading-[1.2] tracking-tight text-[#1C1C1A]">
            I tuoi dati sono tuoi.<br className="sm:hidden" />
            <span className="italic-accent text-[#7C8B6E]"> E restano qui.</span>
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {[
            {
              icon: Lock,
              title: "Tutto criptato",
              body: "Password in bcrypt, sessioni firmate, traffico HTTPS. Zero eccezioni.",
            },
            {
              icon: Flag,
              title: "Server in Europa",
              body: "Dati ospitati in UE secondo GDPR. Nessun transfer fuori dall'Unione.",
            },
            {
              icon: ShieldCheck,
              title: "I tuoi dati non addestrano nessun modello",
              body: "Le tue foto e i tuoi clienti restano tuoi. Non vengono usati per addestrare modelli di terze parti. Punto.",
            },
          ].map(({ icon: Icon, title, body }, i) => (
            <div
              key={i}
              className="bg-white border border-[#E8E5DE] rounded-xl px-4 py-4 sm:py-5 flex gap-3 sm:gap-3.5"
              data-testid={`privacy-card-${i}`}
            >
              <div className="w-9 h-9 rounded-full bg-[#F1F4ED] text-[#7C8B6E] flex items-center justify-center shrink-0">
                <Icon size={17} weight="regular" />
              </div>
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold text-[#1C1C1A] leading-snug">{title}</div>
                <div className="text-[12px] text-[#6B6A65] mt-1 leading-[1.5]">{body}</div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-5 text-center text-[11.5px] text-[#9A968B] italic">
          Cancelli l'account quando vuoi · tutti i tuoi dati spariscono in 24 ore.
        </p>
      </div>
    </section>
  );
}

function TrePromesse() {
  // iter v29 r137 — 3 blocchi macro che spiegano il VALORE di Pronto in
  // 3 promesse: PARLI (vocale ovunque) · MATERIALI (foto/social/brochure
  // generati dal sopralluogo) · ACQUISISCI (volantino stima → lead).
  // Niente più "AI assistant" generico — Pronto è una SOLA app che
  // gestisce 3 momenti diversi della giornata di un agente.
  const promesse = [
    {
      eyebrow: "Parli, è fatto",
      title: "Tutto a voce. In casa, in macchina, in agenzia.",
      lines: [
        "Sopralluogo a voce in casa.",
        "Detti l'agenda mentre guidi.",
        "Messaggio WhatsApp al cliente da voce.",
        "Cambi il prezzo a voce.",
        "Note sul cliente a voce.",
      ],
      kicker: '"Pronto, manda foto del trilocale a Andrea." Fatto.',
      accent: "#7C8B6E",
    },
    {
      eyebrow: "Materiali, già fatti",
      title: "Niente più ore in serata a sistemare cose.",
      lines: [
        "Foto migliorate.",
        "Stanze datate → restyling.",
        "Stanze vuote → arredate.",
        "Annuncio + 8 post + reel.",
        "Email proprietario + vetrina personale online.",
      ],
      kicker: "Tutto generato dal sopralluogo. Niente da scrivere.",
      accent: "#B57C5E",
    },
    {
      eyebrow: "Acquisisci proprietari",
      title: "Trovi nuovi annunci senza inseguire i clienti.",
      lines: [
        "Volantino stima brandizzato col Tuo QR.",
        "Stampato in agenzia oppure inviato online.",
        "Il proprietario fa la stima in 30 secondi.",
        "Tu ricevi il contatto qualificato.",
        "Strumento di acquisizione, non solo carta.",
      ],
      kicker: "Non solo gestisci. Trovi.",
      accent: "#5A6A4D",
    },
  ];
  return (
    <section
      className="py-14 sm:py-20 lg:py-24 bg-[#FAF8F4] border-y border-[#EAE7DF]"
      data-testid="tre-promesse"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10 sm:mb-14 max-w-2xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-[#7C8B6E] mb-3">
            Una sola app · tre cose che oggi fai in cinque
          </div>
          <h2 className="font-editorial text-3xl sm:text-4xl lg:text-5xl text-[#1C1C1A] leading-[1.08] tracking-tight">
            Pronto fa il lavoro<br/>
            <span className="italic-accent text-[#7C8B6E]">che ti porta via le serate.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
          {promesse.map((p, i) => (
            <div
              key={i}
              className="reveal bg-white rounded-2xl border border-[#EAE7DF] p-6 lg:p-7 flex flex-col gap-4 transition-all hover:shadow-[0_18px_44px_-22px_rgba(28,28,26,0.20)] hover:-translate-y-1"
              style={{"--reveal-delay": `${i*120}ms`}}
              data-testid={`promessa-${i+1}`}
            >
              <div className="flex items-baseline gap-3 pb-3 border-b border-[#EAE7DF]">
                <span
                  className="text-[10px] uppercase tracking-[0.24em] font-bold tabular-nums"
                  style={{ color: p.accent }}
                >
                  0{i+1}
                </span>
                <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#7C8B6E]">
                  {p.eyebrow}
                </span>
              </div>
              <h3 className="font-editorial text-[22px] sm:text-[24px] lg:text-[26px] text-[#1C1C1A] leading-[1.18] tracking-tight">
                {p.title}
              </h3>
              <ul className="space-y-2 mt-1 flex-grow">
                {p.lines.map((l, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-[14px] text-[#3F3D38] leading-snug">
                    <span
                      className="shrink-0 mt-1.5 inline-block w-1.5 h-1.5 rounded-full"
                      style={{ background: p.accent }}
                    />
                    {l}
                  </li>
                ))}
              </ul>
              <div
                className="mt-2 px-3.5 py-3 rounded-lg text-[13px] leading-snug font-medium"
                style={{
                  background: `${p.accent}14`,
                  color: "#1C1C1A",
                  borderLeft: `3px solid ${p.accent}`,
                }}
              >
                {p.kicker}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 sm:mt-12 text-center">
          <p className="text-[14px] sm:text-[15px] text-[#3F3D38] max-w-2xl mx-auto leading-relaxed">
            <span className="font-semibold text-[#1C1C1A]">Un'unica app, facile e intuitiva.</span>
            {" "}Niente add-on, niente abbonamenti separati. Quello che oggi fai con cinque programmi diversi, qui lo fai in uno.
          </p>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const items = [
    { n: "+3×", l: "click sull'annuncio grazie a foto e testi curati" },
    { n: "+40%", l: "richieste di visita in media sugli annunci ottimizzati" },
    { n: "60s", l: "dal sopralluogo all'annuncio pronto da pubblicare" },
    { n: "2", l: "portali assistiti: Idealista e Immobiliare.it" },
  ];
  return (
    <section className="py-10 sm:py-14 lg:py-16 bg-white border-y border-[#E5E7E0]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10">
          <h3 className="font-editorial text-2xl lg:text-3xl tracking-tight text-[#1C1C1A]">
            Più richieste. <span className="italic-accent text-[#7C8B6E]">Più vendite.</span>
          </h3>
          <p className="mt-2 text-sm text-[#6B6A65]">
            Un annuncio ben fatto chiude più contratti. Pronto lo fa per ogni tuo immobile.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-6 text-center">
          {items.map((s, i) => (
            <div key={i} className="reveal" style={{"--reveal-delay": `${i*100}ms`}} data-testid={`stat-${i}`}>
              <div className="font-editorial text-4xl lg:text-5xl tracking-tight text-[#7C8B6E] leading-none">{s.n}</div>
              <div className="mt-3 text-[13px] text-[#6B6A65] leading-snug max-w-[200px] mx-auto">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ValueProps() {
  const props = [
    {
      tag: "Più attenzione",
      title: "Foto che fanno cliccare.",
      body: "Il primo scroll su Idealista dura 1,7 secondi. Se la foto è scura o storta, passi oltre. Pronto sistema luce e prospettiva, mantiene la stanza fedele. Risultato: più click sul tuo annuncio.",
    },
    {
      tag: "Più richieste",
      title: "Testi che convincono.",
      body: "Descrizioni scritte in italiano editoriale (niente cliché, niente anglicismi). Un annuncio curato comunica serietà dell'agente, attira clienti seri e riduce i contatti-spam.",
    },
    {
      tag: "Più vendite",
      title: "Meno tempo su cose inutili.",
      body: "Da un vocale + qualche foto all'annuncio pronto da pubblicare su Idealista e Immobiliare in pochi minuti. Il tempo che prima perdevi a fare foto, scrivere e copia-incollare, ora lo usi per chiudere contratti.",
    },
  ];
  return (
    <section id="value-props" className="py-12 sm:py-16 lg:py-20 bg-[#7C8B6E] text-[#FFFFFF]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.22em] text-[#FFFFFF]/60 font-medium">Il vero punto</span>
          <h2 className="font-editorial text-[28px] sm:text-4xl lg:text-5xl mt-3 sm:mt-4 leading-[1.15] tracking-tight">
            Annunci migliori,<br/>
            <span className="italic text-[#F1F4ED]">più vendite.</span>
          </h2>
          <p className="mt-5 text-[15px] text-[#FFFFFF]/75 leading-relaxed max-w-xl">
            Un annuncio curato riceve più aperture, più richieste di visita e chiude più contratti.
            <strong className="text-[#FFFFFF]"> Pronto fa la differenza su ogni immobile che pubblichi</strong>, senza ore in più al telefono o in studio.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {props.map((p, i) => (
            <div
              key={i}
              className="reveal border border-[#FFFFFF]/15 p-7 hover:border-[#FFFFFF]/40 transition-colors"
              style={{"--reveal-delay": `${i*120}ms`}}
              data-testid={`value-prop-${i}`}
            >
              <span className="text-xs uppercase tracking-[0.18em] text-[#7C8B6E] font-semibold">{p.tag}</span>
              <h3 className="font-editorial text-2xl mt-3 leading-tight">{p.title}</h3>
              <p className="mt-4 text-sm text-[#FFFFFF]/70 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pain() {
  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-[#F1F4ED]">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <span className="overline">Per agenti immobiliari</span>
        <h2 className="font-editorial text-[28px] sm:text-4xl lg:text-5xl mt-3 sm:mt-4 leading-[1.15] tracking-tight">
          Se fai l'agente immobiliare,<br/>
          <span className="italic-accent text-[#7C8B6E]">lo sai già.</span>
        </h2>
        <div className="mt-8 space-y-5 text-[17px] text-[#1C1C1A] leading-relaxed">
          <p>
            Le foto dei sopralluoghi non sono quasi mai perfette. Letti disfatti. Oggetti in giro.
            Poca luce. Prospettive storte.
          </p>
          <p>E spesso quelle foto finiscono comunque negli annunci online.</p>
          <p className="text-[#7C8B6E] font-medium">
            Le foto decidono se un cliente ti chiama, o continua a scorrere.
          </p>
        </div>
      </div>
    </section>
  );
}

function WhatItDoesCard({ icon: Icon, tag, title, body, i, open, onToggle }) {
  // Stagger reveal solo per i primi 6 (sopra il fold) per evitare jank
  // sulle card più in basso quando l'utente scorre velocemente.
  const revealDelay = i < 6 ? `${i * 60}ms` : "0ms";

  // Su mobile l'intera card è tappabile per evitare il problema del target
  // troppo piccolo. Su desktop (sm+) il body è sempre visibile, quindi il
  // tap non serve.
  const handleCardTap = (e) => {
    // Su desktop non gestiamo il tap (body sempre visibile).
    if (window.matchMedia("(min-width: 640px)").matches) return;
    e.preventDefault();
    onToggle(i);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCardTap}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardTap(e);
        }
      }}
      className="reveal card-flat self-start p-4 sm:p-6 bg-white border border-transparent transition-[transform,box-shadow,border-color] duration-300 hover:border-[#7C8B6E] hover:shadow-[0_8px_32px_-12px_rgba(124,139,110,0.25)] hover:-translate-y-0.5 group/card will-change-transform cursor-pointer select-none sm:cursor-default"
      style={{"--reveal-delay": revealDelay}}
      data-testid={`whatitdoes-${i}`}
      aria-expanded={open}
    >
      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#F1F4ED] flex items-center justify-center transition-[background-color,transform] duration-500 group-hover/card:bg-[#7C8B6E] group-hover/card:scale-110 group-hover/card:rotate-[-6deg] flex-shrink-0">
          <Icon size={16} weight="duotone" className="sm:hidden text-[#7C8B6E] transition-colors duration-300 group-hover/card:text-white" />
          <Icon size={20} weight="duotone" className="hidden sm:block text-[#7C8B6E] transition-colors duration-300 group-hover/card:text-white" />
        </div>
        <span className="text-[9.5px] sm:text-[11px] uppercase tracking-[0.14em] sm:tracking-[0.2em] text-[#7C8B6E] font-bold transition-[letter-spacing] duration-300 group-hover/card:tracking-[0.24em] break-words">{tag}</span>
      </div>
      <h3 className="font-editorial text-[15px] sm:text-[19px] leading-[1.2] sm:leading-tight font-semibold text-[#1C1C1A]">{title}</h3>
      {/* Desktop (sm+): body sempre visibile. Mobile: visibile dopo tap sulla card. */}
      <p className="mt-2.5 text-[14px] text-[#6B6A65] leading-relaxed [&_strong]:font-semibold [&_strong]:text-[#1C1C1A] hidden sm:block">{body}</p>
      {open && (
        <p className="mt-2.5 text-[13px] text-[#6B6A65] leading-relaxed [&_strong]:font-semibold [&_strong]:text-[#1C1C1A] sm:hidden">{body}</p>
      )}
      <div
        className="sm:hidden mt-2 text-[11px] uppercase tracking-[0.16em] font-semibold text-[#7C8B6E] inline-flex items-center gap-1 pointer-events-none"
        data-testid={`whatitdoes-toggle-${i}`}
      >
        {open ? "Nascondi" : "Scopri"}
        <span className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}>↓</span>
      </div>
    </div>
  );
}

function WhatItDoes() {
  // Ordine per importanza nel workflow agente: dalla feature più "wow" (foto)
  // alla coda CRM/closing. 9 card nette · niente ripetizioni · niente Recap
  // (assorbito in Agenda) · niente "stessi muri" duplicato (Virtual staging
  // riscritto sotto un'etichetta più chiara).
  // iter v29 r231 — User M1388: «la landing è troppo scritta sotto i chip
  // animati». Asciugati tutti i body: rimosso bold sparso, una riga sola,
  // massimo 10-14 parole. Titoli invariati, animazioni in alto fanno il
  // grosso del lavoro; questi testi servono solo a dare nome alla funzione.
  const features = [
    { icon: Sun, tag: "Foto", title: "Da scatto al volo a foto pubblicabile.", body: "Luce, linee, ordine. Sistemata in pochi secondi, pronta per stampa e portali." },
    { icon: PencilSimpleLine, tag: "Annuncio", title: "Testi pronti per ogni portale.", body: "Descrizione editoriale + versioni per Idealista, Immobiliare.it, email e WhatsApp." },
    { icon: Eye, tag: "Sopralluogo", title: "Esci di casa con tutto già pronto.", body: "Parli al telefono col cliente. Esci con bozza annuncio, mappa servizi e niente serata al CRM." },
    { icon: ChatCircleDots, tag: "Agenda", title: "«Pranzo con Bianchi domani 13». Fatto.", body: "Parli al microfono, Pronto crea cliente, promemoria e appuntamento. Sync Google/Apple Calendar." },
    { icon: AddressBook, tag: "Contatti", title: "Chi può comprarlo è già in rubrica.", body: "Pronto incrocia tipo, zona e budget coi tuoi clienti. Apre WhatsApp con messaggio scritto." },
    { icon: FilePdf, tag: "Brand", title: "Tutto col tuo logo.", body: "Annuncio online, volantino A5 col QR, post Instagram: ogni cosa col logo della tua agenzia." },
    { icon: House, tag: "Pianta", title: "Catastale arredata 2D.", body: "Carichi la pianta grezza, scegli uno stile, ricevi un render da rivista per annuncio e portali." },
    { icon: Armchair, tag: "Arredo virtuale", title: "Arredo nuovo, casa la stessa.", body: "Stanze vuote o mobili anni '90 diventano ambienti curati. Solo l'arredo cambia, mai le strutture." },
    { icon: WhatsappLogo, tag: "Messaggi WhatsApp", title: "Stesso messaggio, scritto per ognuno.", body: "Auguri o saluti a tutta la rubrica: Pronto personalizza nome, città e dettagli per ciascuno." },
    { icon: Calculator, tag: "Stima", title: "Valore immobile in 30 secondi.", body: "Indirizzo e metri. Pronto restituisce un range di prezzo realistico dagli annunci vicini." },
    { icon: FilmStrip, tag: "Reel · in arrivo", title: "Montaggio video automatico.", body: "Pronto taglia i silenzi, sincronizza scritte con la voce e monta musica. In arrivo." },
  ];
  return (
    <section id="cosa-fa" className="py-12 sm:py-16 lg:py-20 bg-[#FFFFFF]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <span className="overline">Cosa fa Pronto</span>
          <h2 className="font-editorial text-[26px] sm:text-3xl lg:text-4xl mt-3 sm:mt-4 leading-[1.15] tracking-tight">
            Tutto quello che serve all'agente.<br/>
            <span className="italic-accent text-[#7C8B6E]">In un'unica app.</span>
          </h2>
        </div>
        <WhatItDoesGrid features={features} />
      </div>
    </section>
  );
}

function WhatItDoesGrid({ features }) {
  // Solo una card aperta alla volta su mobile. Lo stato vive nel parent per
  // evitare che aprire una card lasci l'altra aperta (problema "torna alle
  // chip" segnalato dall'utente: troppe card aperte facevano saltare il
  // layout in alto).
  const [openIndex, setOpenIndex] = useState(null);
  const handleToggle = (idx) => {
    setOpenIndex((prev) => (prev === idx ? null : idx));
  };
  return (
    <div className="mt-10 grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-5 items-start">
      {features.map(({ icon, tag, title, body }, i) => (
        <WhatItDoesCard
          key={i}
          icon={icon}
          tag={tag}
          title={title}
          body={body}
          i={i}
          open={openIndex === i}
          onToggle={handleToggle}
        />
      ))}
    </div>
  );
}

function HowItWorks() {
  const steps = [
    { n: "1", t: "Registra", b: "Una nota vocale al sopralluogo. Pronto inserisce ogni dato al posto giusto." },
    { n: "2", t: "Carica", b: "Le foto del telefono e la pianta catastale (se ce l'hai)." },
    { n: "3", t: "Annuncio pronto", b: "Annuncio scritto, vetrina personale e social pronti — le foto le migliori tu con un tocco quando vuoi." },
    { n: "4", t: "Pubblica", b: "L'estensione Chrome ti aiuta su Idealista e Immobiliare.it. Foto, testi e dati già pronti." },
  ];
  return (
    <section id="come-funziona" className="py-12 sm:py-16 lg:py-20 bg-[#F1F4ED]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="max-w-xl">
          <span className="overline">Come funziona</span>
          <h2 className="font-editorial text-[26px] sm:text-3xl lg:text-4xl mt-3 sm:mt-4 leading-[1.15] tracking-tight">
            Dal sopralluogo<br/>
            <span className="italic-accent text-[#7C8B6E]">all'annuncio.</span>
          </h2>
        </div>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5">
          {steps.map((s, i) => (
            <div key={i} className="reveal bg-white p-5 lg:p-6 border border-[#E5E7E0]" style={{"--reveal-delay": `${i*80}ms`}} data-testid={`step-${i}`}>
              <div className="font-editorial text-3xl lg:text-5xl text-[#7C8B6E] leading-none">{s.n}</div>
              <h3 className="font-editorial text-lg lg:text-2xl mt-3">{s.t}</h3>
              <p className="mt-1.5 text-[13px] lg:text-[15px] text-[#6B6A65] leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RinnovaArredoSection() {
  // Mini-sezione standalone: la card "Rinnova arredo" con una singola slide
  // dedicata. Niente duplicazione del selettore dell'hero.
  return (
    <section className="pt-4 pb-16 lg:pb-24 bg-[#FFFFFF]">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-8">
          <span className="overline">Rinnova arredo · novità</span>
          <h2 className="reveal font-editorial text-[26px] sm:text-3xl lg:text-4xl mt-3 leading-[1.15] tracking-tight">
            Una casa vissuta<br/>
            <span className="italic-accent text-[#7C8B6E]">torna nuova.</span>
          </h2>
          <p className="reveal mt-3 text-[14px] sm:text-[15px] text-[#6B6A65] max-w-md mx-auto leading-relaxed">
            Senza ristrutturare niente. I muri restano, l'arredo si rinnova nello stile che scegli.
          </p>
        </div>

        <div className="max-w-xl mx-auto">
          <BeforeAfterSlider
            beforeSrc="/landing/rinnova-before.jpg"
            afterSrc="/landing/rinnova-after.jpg"
            height="h-[340px] lg:h-[440px]"
          />
        </div>

        <div className="mt-8 max-w-md mx-auto bg-[#F1F4ED] border border-[#E5E7E0] rounded-lg px-4 py-4 text-left" data-testid="rinnova-arredo-card">
          <ul className="space-y-1.5 text-[12.5px] sm:text-[13px] text-[#1C1C1A] leading-relaxed">
            <li className="flex gap-2"><span className="text-[#7C8B6E] flex-shrink-0">✓</span><span><strong>Lo mostri dal vivo al cliente</strong> durante il sopralluogo — "ecco com'è, ecco come può diventare".</span></li>
            <li className="flex gap-2"><span className="text-[#7C8B6E] flex-shrink-0">✓</span><span>Sostituisce arredi datati con pezzi moderni nello stile scelto.</span></li>
            <li className="flex gap-2"><span className="text-[#7C8B6E] flex-shrink-0">✓</span><span>Cambia anche <strong>pavimento, tende, parquet</strong> se chiesto.</span></li>
            <li className="flex gap-2"><span className="text-[#7C8B6E] flex-shrink-0">✓</span><span><strong>Muri, finestre, planimetria</strong> restano identici al millimetro.</span></li>
            <li className="flex gap-2"><span className="text-[#7C8B6E] flex-shrink-0">✓</span><span>Perfetto per case ereditate, ex affitti, immobili anni '70-'90.</span></li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function Gallery() {
  // Compat legacy: reindirizzo alla nuova RinnovaArredoSection.
  // Se qualche testid o snapshot referenziava Gallery, lo trova comunque.
  return <RinnovaArredoSection />;
}

function PlanFurnish() {
  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-[#F1F4ED]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <span className="overline">Pianta arredata · esclusivo Pronto</span>
          <h2 className="font-editorial text-[28px] sm:text-4xl lg:text-5xl mt-3 sm:mt-4 leading-[1.15] tracking-tight">
            La pianta catastale,<br/>
            <span className="italic-accent text-[#7C8B6E]">finalmente arredata.</span>
          </h2>
          <p className="mt-5 text-[16px] text-[#1C1C1A] leading-relaxed">
            Carica la pianta catastale: leggiamo le etichette delle stanze (CUCINA, CAMERA, BAGNO…) e
            te la restituiamo arredata 2D, vista dall'alto, stile rivista di lusso. Stessi muri, stesse scale.
          </p>
          <p className="mt-3 text-sm text-[#6B6A65]">
            Le stanze senza etichetta restano vuote — niente arredi inventati.
          </p>
          <p className="mt-6 text-xs text-[#6B6A65] tracking-widest uppercase">↓ Esempio</p>
        </div>
        <div className="lg:col-span-7">
          <div className="card-flat bg-white p-4 lg:p-6 overflow-hidden">
            <FloorPlanMockup height={460} />
          </div>
        </div>
      </div>
    </section>
  );
}

function ShareWithClient() {
  return (
    <>
      <SendProntoSection />
      {/* Iter 242 — RoadmapSection ("Contenuti pronti ogni giorno") spostato
          a livello root della Landing, sopra ProntoVsAltri, per coerenza
          narrativa: prima mostriamo cosa si produce, poi il confronto. */}
      <BookingSection />
      <section className="py-12 sm:py-16 lg:py-20 bg-[#F1F4ED]" data-testid="share-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 lg:order-2">
          <span className="overline">Cliente in attesa</span>
          <h2 className="font-editorial text-[28px] sm:text-4xl lg:text-5xl mt-3 sm:mt-4 leading-[1.15] tracking-tight">
            Manda tutto<br/>
            <span className="italic-accent text-[#7C8B6E]">in un secondo.</span>
          </h2>
          <p className="mt-5 text-[16px] text-[#1C1C1A] leading-relaxed">
            Dall'app, con un tap, condividi qualsiasi materiale con il cliente:
            <strong> vetrina personale, foto migliorate, piantine catastali arredate</strong>.
            Direttamente su WhatsApp, email o link. Senza scaricare, comprimere, allegare.
          </p>
          <ul className="mt-6 space-y-2.5 text-[15px] text-[#1C1C1A]">
            <li className="flex gap-3"><span className="text-[#7C8B6E] flex-shrink-0">✓</span> Link unico con tutto il materiale</li>
            <li className="flex gap-3"><span className="text-[#7C8B6E] flex-shrink-0">✓</span> Vetrina personale stile rivista, brandizzata con il logo della tua agenzia</li>
            <li className="flex gap-3"><span className="text-[#7C8B6E] flex-shrink-0">✓</span> Singola foto, ZIP completo o link unico — tu scegli</li>
          </ul>
        </div>
        <div className="lg:col-span-7 lg:order-1">
          <div className="card-flat p-7 lg:p-9 bg-white">
            {/* WhatsApp-style message preview */}
            <div className="bg-[#FAF9F4] rounded-2xl p-5 max-w-md mx-auto" data-testid="share-preview">
              <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-[#E5E7E0]">
                <div className="w-8 h-8 rounded-full bg-[#25D366] flex items-center justify-center text-white font-bold text-sm">W</div>
                <div>
                  <div className="text-sm font-semibold text-[#1C1C1A]">Cliente Marco</div>
                  <div className="text-[10px] text-[#6B6A65] uppercase tracking-widest">online ora</div>
                </div>
              </div>
              <div className="bg-[#DCF8C6] rounded-2xl rounded-tr-sm p-3.5 text-sm text-[#1C1C1A] leading-relaxed">
                Marco, ti mando tutto sul trilocale di Piazza Navona 👇
              </div>
              <div className="mt-2 bg-white border border-[#E5E7E0] rounded-2xl rounded-tr-sm p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#7C8B6E] flex items-center justify-center text-white text-xl">🏠</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#1C1C1A] truncate">Vetrina personale · Piazza Navona</div>
                  <div className="text-xs text-[#6B6A65]">link unico · stile rivista</div>
                </div>
              </div>
              <div className="mt-2 bg-white border border-[#E5E7E0] rounded-2xl rounded-tr-sm p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#1C1C1A] flex items-center justify-center text-white text-lg">🖼</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#1C1C1A] truncate">12 foto migliorate + pianta arredata</div>
                  <div className="text-xs text-[#6B6A65]">link unico · scade tra 30 giorni</div>
                </div>
              </div>
              <div className="mt-3 text-[11px] text-[#6B6A65] text-center">
                Tap & invio. Tutto qui.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
}

function BookingSection() {
  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white border-b border-[#E5E7E0]" data-testid="booking-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Mockup telefono cliente che prenota */}
        <div className="lg:col-span-6 lg:order-1 order-2">
          <div className="max-w-[320px] mx-auto">
            {/* iPhone 17 Pro titanio + Dynamic Island grande */}
            <div
              className="relative rounded-[44px] p-[3px]"
              style={{
                background: "linear-gradient(135deg, #2A2A28 0%, #1C1C1A 50%, #2F2F2D 100%)",
                boxShadow: "0 32px 64px -20px rgba(28,28,26,0.45), 0 8px 16px -8px rgba(28,28,26,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
              }}
            >
              <div className="bg-white rounded-[40px] overflow-hidden relative">
                {/* Dynamic Island iPhone 17 Pro */}
                <div className="absolute top-[12px] left-1/2 -translate-x-1/2 w-[100px] h-[26px] bg-black rounded-full z-20" />
                {/* Specular highlight */}
                <div className="absolute top-0 left-0 right-0 h-[30%] pointer-events-none z-[5]"
                     style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.05), transparent)" }} />
                {/* Annuncio mini */}
                <div className="aspect-[3/4] bg-cover bg-center relative" style={{ backgroundImage: "url('/landing/sala-original-after.jpg')" }}>
                  <div className="absolute inset-x-0 bottom-0 h-3/5" style={{ background: "linear-gradient(to top, rgba(124,139,110,0.96) 0%, rgba(124,139,110,0.6) 50%, transparent 100%)" }} />
                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <div className="font-editorial text-[15px] leading-tight drop-shadow">Trilocale Brera</div>
                    <div className="text-[10px] opacity-90">100 mq · 3 locali · € 890.000</div>
                  </div>
                </div>
                {/* Sticky bottom CTA cliente */}
                <div className="px-3 py-2.5 bg-white border-t border-[#E5E7E0]">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-[8px] uppercase tracking-[0.18em] font-bold text-[#9A968B] leading-none">In vendita</div>
                      <div className="font-editorial text-[12px] text-[#1C1C1A]">€ 890.000</div>
                    </div>
                    <div className="inline-flex items-center gap-1 bg-[#7C8B6E] text-white text-[10px] font-semibold px-3 py-2 rounded-md shadow">
                      📅 Prenota visita
                    </div>
                  </div>
                </div>
                {/* Form preview */}
                <div className="bg-[#FAF9F4] border-t border-[#E5E7E0] px-3 pt-3 pb-4 space-y-2">
                  <div className="text-[8px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E]">Quando preferisci?</div>
                  <div className="flex gap-1 overflow-hidden">
                    {["Mar", "Mer", "Gio"].map((d, i) => (
                      <div key={d} className={`shrink-0 px-2 py-1 text-[9px] rounded border ${i === 1 ? "bg-[#1C1C1A] text-white border-[#1C1C1A]" : "bg-white text-[#1C1C1A] border-[#E5E7E0]"}`}>{d} {i+8}</div>
                    ))}
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {["10:00", "11:30", "15:00", "16:30"].map((t, i) => (
                      <div key={t} className={`px-1.5 py-0.5 text-[9px] rounded-full border ${i === 2 ? "bg-[#7C8B6E] text-white border-[#7C8B6E]" : "bg-white text-[#1C1C1A] border-[#E5E7E0]"}`}>{t}</div>
                    ))}
                  </div>
                  <div className="bg-[#7C8B6E] text-white text-[10px] font-bold py-1.5 rounded text-center">Invia richiesta</div>
                </div>
              </div>
            </div>
            <div className="mt-3 text-center text-[11px] text-[#6B6A65] italic">Il cliente prenota dal suo telefono</div>
          </div>
        </div>

        {/* Testo destra */}
        <div className="lg:col-span-6 lg:order-2 order-1">
          <span className="overline">Visite senza telefono</span>
          <h2 className="font-editorial text-[28px] sm:text-4xl lg:text-5xl mt-3 sm:mt-4 leading-[1.15] tracking-tight">
            Il cliente prenota la visita<br/>
            <span className="italic-accent text-[#7C8B6E]">direttamente dall'annuncio.</span>
          </h2>
          <p className="mt-5 text-[16px] text-[#1C1C1A] leading-relaxed">
            Sull'annuncio pubblico c'è un bottone <strong>"Prenota una visita"</strong>. Il cliente sceglie giorno e orario,
            tu ricevi una <strong>notifica</strong> sul telefono e la visita entra <strong>automaticamente in agenda</strong>.
            Niente più telefonate per concordare l'orario.
          </p>
          <ul className="mt-6 space-y-2.5 text-[15px] text-[#1C1C1A]">
            <li className="flex gap-3"><span className="text-[#7C8B6E] flex-shrink-0">✓</span> Niente registrazione per il cliente · 30 secondi e ha prenotato</li>
            <li className="flex gap-3"><span className="text-[#7C8B6E] flex-shrink-0">✓</span> Push istantanea quando arriva una richiesta</li>
            <li className="flex gap-3"><span className="text-[#7C8B6E] flex-shrink-0">✓</span> Tu confermi, riprogrammi o annulli con un tap</li>
          </ul>
          <p className="mt-6 text-[12.5px] text-[#9A968B] italic leading-snug">
            Una visita prenotata è il segnale più forte che hai un cliente caldo. Pronto la traccia per te.
          </p>
        </div>
      </div>
    </section>
  );
}

function SendProntoSection() {
  return (
    <section id="contatti" className="py-12 sm:py-16 lg:py-20 bg-white border-y border-[#E5E7E0]" data-testid="send-pronto-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-6">
          <span className="overline">Contatti & WhatsApp</span>
          <h2 className="font-editorial text-[28px] sm:text-4xl lg:text-5xl mt-3 sm:mt-4 leading-[1.15] tracking-tight">
            Ogni nuovo annuncio è<br/>
            <span className="italic-accent text-[#7C8B6E]">una telefonata che parte.</span>
          </h2>
          <p className="mt-5 text-[15.5px] sm:text-[16px] text-[#1C1C1A] leading-relaxed">
            Importa la rubrica del telefono <span className="text-[#6B6A65]">(.vcf da iPhone/Android)</span>, un Excel di clienti vecchi, o aggiungi a mano. Per ognuno salvi cosa cerca: tipologia, zona, budget.
          </p>
          <p className="mt-3 text-[15px] text-[#1C1C1A] leading-relaxed">
            Quando carichi un nuovo immobile, Pronto incrocia tutto e ti mostra <strong>chi della tua rubrica lo stava già cercando</strong>. Per ogni cliente prepara il messaggio WhatsApp scritto già col suo nome.
          </p>
          <ul className="mt-6 space-y-2.5 text-[14px] text-[#1C1C1A]">
            {[
              { icon: AddressBook, text: "Rubrica con preferenze: tipologia, zona, budget, mq" },
              { icon: Sparkle, text: "Abbinamento automatico al nuovo annuncio (punteggio + motivi)" },
              { icon: WhatsappLogo, text: "Messaggio WhatsApp pre-scritto, personalizzato col nome del cliente" },
              { icon: ShieldCheck, text: "Niente bulk send: 1 conversazione alla volta. Anti-spam by design" },
            ].map(({ icon: Icon, text }, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <Icon size={16} weight="duotone" className="text-[#7C8B6E] mt-0.5 shrink-0" />
                <span>{text}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-[12.5px] text-[#9A968B] italic">
            Quanti clienti compatibili appariranno? Dipende solo dalla tua rubrica. Se hai 200 clienti con preferenze, le proposte saranno proporzionali. Pronto non inventa numeri.
          </p>
        </div>
        <div className="lg:col-span-6">
          <ContactsMatchMockup />
        </div>
      </div>
    </section>
  );
}

function Portali() {
  // Deprecato iter v32 r26 — sostituito da `PortaliAutopilotFlow` che racconta
  // il flusso completo "annuncio dentro Pronto → estensione → portali" con
  // mockup browser + pannello laterale animati.
  return null;
}

function FinalCTA() {
  const navigate = useNavigate();
  return (
    <section className="py-16 lg:py-24 bg-[#7C8B6E] text-[#FFFFFF]">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <h2 className="font-editorial text-4xl lg:text-6xl leading-[1.05] tracking-tighter">
          Prova sul tuo<br/>
          <span className="italic text-[#F1F4ED]">prossimo immobile.</span>
        </h2>
        <p className="mt-6 text-lg opacity-85 max-w-lg mx-auto">
          Una foto, un vocale. Vedi cosa esce. Se non ti convince, chiudi la scheda.
        </p>
        <div className="mt-10 flex items-center justify-center gap-4 flex-wrap">
          <button onClick={() => navigate("/register")} className="bg-[#1C1C1A] hover:bg-black text-white px-7 py-3 rounded-sm font-medium inline-flex items-center gap-2 transition-colors" data-testid="final-cta">
            Iscrizione gratuita  <ArrowRight size={18} weight="bold" />
          </button>
          <button onClick={() => navigate("/prova")} className="border border-[#FFFFFF]/30 text-[#FFFFFF] px-7 py-3 rounded-sm font-medium hover:bg-[#FFFFFF]/10 transition-colors" data-testid="final-cta-pricing">
            Provala senza registrarti
          </button>
        </div>
        <p className="mt-5 text-sm opacity-75">Gratis · senza carta · privacy e sicurezza</p>
      </div>
    </section>
  );
}

// FAQ_ITEMS moved to /app/frontend/src/pages/landing/LandingFAQ.jsx

function Trust() {
  const items = [
    {
      icon: Lock,
      title: "Dati criptati end-to-end",
      body: "Password bcrypt, sessioni firmate, traffico HTTPS. Le tue foto e i dati dei clienti sono al sicuro.",
    },
    {
      icon: Flag,
      title: "Server e dati in Europa",
      body: "Archiviazione in cloud EU, conforme GDPR. I dati non escono dallo spazio europeo e non vengono ceduti a terzi.",
    },
    {
      icon: ShieldCheck,
      title: "Fidelity Guard integrato",
      body: "Niente invenzioni: un sistema di controllo verifica ogni foto generata. Mobili, colori e struttura restano fedeli all'immobile reale.",
    },
    {
      icon: CheckCircle,
      title: "Watermark legale D.Lgs 70/2003",
      body: "Ogni foto modificata digitalmente è marcata con una dichiarazione di trasparenza, come richiede la legge italiana. Tu sei già a norma, il cliente non viene mai ingannato.",
    },
  ];
  return (
    <section id="trust" className="py-12 sm:py-16 lg:py-20 bg-white border-y border-[#E5E7E0]" data-testid="trust-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <span className="overline">Sicurezza & affidabilità</span>
          <h2 className="font-editorial text-[28px] sm:text-4xl lg:text-5xl mt-3 sm:mt-4 leading-[1.15] tracking-tight">
            Un'app <span className="italic-accent text-[#7C8B6E]">di cui fidarsi.</span>
          </h2>
          <p className="mt-4 text-[15px] text-[#6B6A65] leading-relaxed">
            Lavori con dati sensibili: foto di interni, prezzi, contatti di clienti. Pronto è costruita con
            standard professionali di sicurezza e conformità.
          </p>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-7 lg:gap-8">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <div key={i} className="flex gap-5 items-start" data-testid={`trust-item-${i}`}>
                <div className="w-12 h-12 rounded-full bg-[#F1F4ED] flex items-center justify-center flex-shrink-0">
                  <Icon size={22} weight="regular" className="text-[#7C8B6E]" />
                </div>
                <div>
                  <h3 className="font-editorial text-xl tracking-tight text-[#1C1C1A]">{it.title}</h3>
                  <p className="mt-2 text-[14px] text-[#6B6A65] leading-relaxed">{it.body}</p>
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-14 text-center text-xs uppercase tracking-[0.2em] text-[#6B6A65]">
          GDPR · HTTPS · Server EU · Bcrypt · Stripe PCI-DSS
        </p>
      </div>
    </section>
  );
}

function RoadmapSection() {
  return (
    <section className="py-14 sm:py-20 lg:py-24 bg-[#FAFAF7] border-y border-[#EDEAE2]" data-testid="extras-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">

        {/* Storia del giorno showcase — sage gradient background */}
        <div
          className="reveal grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 items-center rounded-2xl p-6 sm:p-10 lg:p-12"
          style={{
            background: "linear-gradient(135deg, #E8EDDF 0%, #F1F4ED 45%, #FAFBF7 100%)",
            border: "1px solid rgba(124, 139, 110, 0.18)",
          }}
          data-testid="extra-story"
        >
          <div className="lg:col-span-7 order-1">
            <StoryMockup />
          </div>
          <div className="lg:col-span-5 order-2">
            <span className="inline-block px-2.5 py-1 rounded-full bg-[#7C8B6E] text-white text-[10px] uppercase tracking-[0.2em] font-semibold mb-4">
              Contenuti pronti, ogni giorno
            </span>
            <h4 className="font-editorial text-[24px] sm:text-3xl text-[#1C1C1A] leading-tight tracking-tight mb-3">
              Una storia al mattino,<br />
              <span className="italic-accent text-[#7C8B6E]">una casa arredata nel pomeriggio.</span>
            </h4>
            <p className="text-[14.5px] sm:text-[15px] text-[#3F3D38] leading-relaxed mb-4">
              Apri Pronto col caffè e c'è già una grafica 9:16 pronta — tua foto migliore,
              prezzo, indirizzo, logo. Un tocco, la condividi su stato WhatsApp o storia Instagram.
              Quando rientri, una foto della casa vuota diventa arredata in 5 secondi:
              il cliente vede il potenziale, tu chiudi prima.
            </p>
            <ul className="space-y-2 mb-5">
              {[
                "Foto già migliorata · prezzo a vista · zero scritte fuori",
                "Tutto col tuo logo · zero traccia di Pronto",
                "WhatsApp, Instagram, Telegram · 1 tocco",
              ].map((s, i) => (
                <li key={i} className="flex gap-2 text-[13px] text-[#3F3D38]">
                  <CheckCircle size={15} weight="fill" className="text-[#7C8B6E] flex-shrink-0 mt-0.5" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#5A6A4D] font-semibold">
              1080×1920 png · pronto in 2 secondi · scaricabile o condivisibile
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * StoryMockup — telefono verticale che alterna 2 contenuti, in loop:
 *
 *  1. **STORY DI OGGI** (4s)
 *     - Progress bar 0→100% in 4s
 *     - Ken Burns sulla foto cucina migliorata
 *     - Title / sub / prezzo entrano staggered
 *     - Badge IN VENDITA pulsa
 *
 *  2. **ARREDA SPAZIO VUOTO** (6s)
 *     - Stessa cornice phone
 *     - 2.0s di stanza VUOTA (l'utente vede chiaramente lo spazio nudo)
 *     - 1.2s di crossfade vuoto → arredato
 *     - 2.0s di stanza ARREDATA + caption "Spazio vuoto → arredato in 5 secondi"
 *     - Caption "Spazio vuoto → arredato" + label "AI staging"
 *
 * Niente video, niente file pesanti — tutto CSS keyframes (animation 10s
 * totali, infinite). Trigger: IntersectionObserver, parte solo a vista.
 */
function StoryMockup() {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { setActive(true); obs.disconnect(); }
      }),
      { threshold: 0.4 },
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="flex justify-center" data-testid="story-mockup">
      <style>{`
        /* Total cycle: 10s (4s story + 6s staging con respiro su stanza vuota) */
        @keyframes story-cycle {
          0%, 40%   { opacity: 1; }
          43%, 97%  { opacity: 0; }
          100%      { opacity: 1; }
        }
        @keyframes staging-cycle {
          0%, 40%   { opacity: 0; }
          43%, 97%  { opacity: 1; }
          100%      { opacity: 0; }
        }
        @keyframes story-progress {
          0%   { width: 0%; }
          40%  { width: 100%; }
          100% { width: 0%; }
        }
        @keyframes story-kenburns {
          0%   { transform: scale(1.0)  translate(0, 0); }
          40%  { transform: scale(1.05) translate(-1.2%, -0.8%); }
          100% { transform: scale(1.0)  translate(0, 0); }
        }
        @keyframes story-fade-up {
          0%, 5%   { opacity: 0; transform: translateY(8px); }
          15%, 40% { opacity: 1; transform: translateY(0); }
          43%, 100%{ opacity: 0; transform: translateY(0); }
        }
        @keyframes story-tag-pop {
          0%, 11%  { transform: scale(0.8); opacity: 0; }
          18%      { transform: scale(1.08); opacity: 1; }
          22%, 40% { transform: scale(1); opacity: 1; }
          43%, 100%{ opacity: 0; }
        }
        /* Stanza VUOTA visibile da 43% (4.3s) a 63% (6.3s) = 2s di "respiro vuoto" (un po' meno) */
        /* Crossfade vuoto→arredato 63%→75% (1.2s) */
        /* Stanza ARREDATA piena da 75% (7.5s) a 95% (9.5s) = 2s di vetrina arredata + caption */
        @keyframes staging-furnish {
          0%, 63%  { opacity: 0; }
          75%, 95% { opacity: 1; }
          100%     { opacity: 0; }
        }
        @keyframes staging-caption {
          0%, 68%  { opacity: 0; transform: translateY(8px); }
          78%, 95% { opacity: 1; transform: translateY(0); }
          100%     { opacity: 0; transform: translateY(0); }
        }
        .story--anim .story__layer    { animation: story-cycle 10s ease-in-out infinite; }
        .story--anim .stage__layer    { animation: staging-cycle 10s ease-in-out infinite; }
        .story--anim .story__progress { animation: story-progress 10s linear infinite; }
        .story--anim .story__bg       { animation: story-kenburns 10s ease-out infinite; }
        .story--anim .story__title    { animation: story-fade-up 10s ease-in-out infinite; }
        .story--anim .story__sub      { animation: story-fade-up 10s ease-in-out infinite; animation-delay: 200ms; }
        .story--anim .story__price    { animation: story-fade-up 10s ease-in-out infinite; animation-delay: 400ms; }
        .story--anim .story__tag      { animation: story-tag-pop 10s ease-in-out infinite; }
        .story--anim .stage__after    { animation: staging-furnish 10s ease-in-out infinite; }
        .story--anim .stage__caption  { animation: staging-caption 10s ease-in-out infinite; }
      `}</style>
      <div className={`relative ${active ? "story--anim" : ""}`} style={{ width: 260, height: 462 }}>
        {/* iPhone 17 Pro titanio frame */}
        <div
          className="absolute inset-0 rounded-[44px]"
          aria-hidden="true"
          style={{
            background: "linear-gradient(135deg, #2A2A28 0%, #1C1C1A 50%, #2F2F2D 100%)",
            boxShadow: "0 32px 70px -16px rgba(28,28,26,0.45), 0 10px 20px -8px rgba(28,28,26,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
          }}
        />
        {/* Screen */}
        <div className="absolute inset-[3px] rounded-[41px] overflow-hidden bg-[#F1F4ED]">
          {/* Dynamic Island iPhone 17 Pro grande orizzontale */}
          <div className="absolute top-[12px] left-1/2 -translate-x-1/2 w-[96px] h-[26px] rounded-full bg-black z-30" aria-hidden="true" />
          {/* Specular highlight */}
          <div className="absolute top-0 left-0 right-0 h-[30%] pointer-events-none z-[6]"
               style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.05), transparent)" }} aria-hidden="true" />

          {/* ────── LAYER 1 — STORY DI OGGI ────── */}
          <div className="story__layer absolute inset-0" style={{ opacity: active ? undefined : 1 }}>
            <div className="absolute inset-0 overflow-hidden">
              <img
                src="/landing/cucina-after.jpg"
                alt=""
                className="story__bg absolute inset-0 w-full h-full object-cover"
                draggable="false"
                style={{ transformOrigin: "center center" }}
              />
            </div>
            <div
              className="absolute inset-x-0 bottom-0 h-[62%]"
              style={{
                background: "linear-gradient(to top, rgba(76, 92, 64, 0.95) 0%, rgba(124, 139, 110, 0.78) 35%, rgba(124, 139, 110, 0.30) 70%, transparent 100%)",
              }}
              aria-hidden="true"
            />
            {/* Story progress bar top — sotto la Dynamic Island */}
            <div className="absolute top-[46px] left-3 right-3 flex gap-1 z-30" aria-hidden="true">
              <div className="flex-1 h-[2px] bg-white/30 rounded-full overflow-hidden">
                <div className="story__progress h-full bg-white" style={{ width: active ? undefined : "0%" }} />
              </div>
            </div>
            {/* Agency branding — sotto la Dynamic Island e progress bar */}
            <div className="absolute top-[60px] left-3 right-3 flex items-center gap-2 z-20">
              <div className="w-7 h-7 rounded-full bg-white/95 border-2 border-white flex items-center justify-center text-[10px] font-bold text-[#1C1C1A] flex-shrink-0">
                AV
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold text-white leading-tight drop-shadow-md truncate">ada.verzi_immobili</div>
                <div className="text-[9px] text-white/85 leading-tight drop-shadow truncate">2 ore fa</div>
              </div>
            </div>
            {/* IN VENDITA tag — sotto agency, sotto Dynamic Island */}
            <div className="story__tag absolute top-[92px] right-3 px-2 py-0.5 bg-white text-[#5A6A4D] text-[8.5px] uppercase tracking-[0.18em] font-bold rounded shadow-lg z-20" style={{ opacity: active ? undefined : 0 }}>
              In vendita
            </div>
            {/* Title + subtitle */}
            <div className="absolute bottom-5 left-4 right-4 z-10">
              <div className="story__title font-editorial text-white text-[20px] leading-[1.05] drop-shadow-md" style={{ opacity: active ? undefined : 0 }}>
                Trilocale<br />
                <span className="italic-accent text-[#E8EDDF]">in centro a Milano.</span>
              </div>
              <div className="story__sub text-[10px] text-white/90 mt-1.5 leading-snug drop-shadow" style={{ opacity: active ? undefined : 0 }}>
                Brera · Via Solferino · 100 mq · 3 locali
              </div>
              <div className="story__price flex items-center gap-2 mt-2" style={{ opacity: active ? undefined : 0 }}>
                <div className="text-[15px] font-bold text-white drop-shadow">€ 890.000</div>
                <div className="ml-auto text-[8.5px] uppercase tracking-[0.18em] text-white/85 font-semibold">Ada Verzi Immobili</div>
              </div>
            </div>
          </div>

          {/* ────── LAYER 2 — ARREDA SPAZIO VUOTO ────── */}
          <div className="stage__layer absolute inset-0" style={{ opacity: active ? undefined : 0 }} aria-hidden="true">
            {/* Empty room as base */}
            <img
              src="/landing/staging-empty.jpg?v=4"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: '15% center' }}
              draggable="false"
            />
            {/* Furnished overlay — fades in during second half */}
            <img
              src="/landing/staging-furnished.jpg?v=4"
              alt=""
              className="stage__after absolute inset-0 w-full h-full object-cover"
              draggable="false"
              style={{ opacity: active ? undefined : 0, objectPosition: '15% center' }}
            />
            {/* Bottom gradient + caption (entrambi animati: appaiono insieme alla cucina arredata) */}
            <div
              className="stage__caption absolute inset-x-0 bottom-0 h-[40%]"
              style={{
                background: "linear-gradient(to top, rgba(28, 28, 26, 0.92) 0%, rgba(28, 28, 26, 0.55) 50%, transparent 100%)",
                opacity: active ? undefined : 0,
              }}
              aria-hidden="true"
            />
            <div className="stage__caption absolute bottom-5 left-4 right-4 z-10" style={{ opacity: active ? undefined : 0 }}>
              <div className="inline-block px-2 py-0.5 mb-2 bg-[#C8A66B] text-[#1C1C1A] text-[8.5px] uppercase tracking-[0.18em] font-bold rounded shadow-md">
                Virtual staging
              </div>
              <div className="font-editorial text-white text-[20px] leading-[1.05] drop-shadow-md">
                Spazio vuoto<br />
                <span className="italic-accent text-[#E8EDDF]">arredato in 5 secondi.</span>
              </div>
              <div className="text-[10px] text-white/90 mt-1.5 leading-snug drop-shadow">
                Una foto. Niente fotografo. Niente arredatore.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// iter v31 r20 — NEW landing mobile-first.
// La vecchia struttura (Hero con chip/tab + IntersectionObserver buggato)
// è completamente sostituita. I component legacy restano definiti più
// sotto nel file per non rompere il build ma non vengono più renderizzati.
import HeaderV2 from "@/pages/landing/v2/HeaderV2";
import HeroV2 from "@/pages/landing/v2/HeroV2";
import FotoMega from "@/pages/landing/v2/FotoMega";
import StimaPersonalizzaDemo from "@/pages/landing/v2/StimaPersonalizzaDemo";
import SopralluogoMega from "@/pages/landing/v2/SopralluogoMega";
import SocialProofMarquee from "@/pages/landing/v2/SocialProofMarquee";
import BentoGridV2 from "@/pages/landing/v2/BentoGridV2";
import FinalCTAV2 from "@/pages/landing/v2/FinalCTAV2";
import StickyMobileCTA from "@/pages/landing/v2/StickyMobileCTA";
import FeatureBlock from "@/pages/landing/v2/FeatureBlock";
import LiveTryThree from "@/pages/landing/v2/LiveTryThree";
import VecchioMetodo from "@/pages/landing/v2/VecchioMetodo";
import IlSoftwareDellAgente from "@/pages/landing/v2/IlSoftwareDellAgente";
import RenderingCantiere from "@/pages/landing/v2/RenderingCantiere";
// iter v32 r26 — Nuova sezione dedicata al flusso "annuncio dentro Pronto
// → estensione Chrome → portali", con storytelling a 3 step e mockup
// browser+pannello laterale animati. Sostituisce il vecchio blocco Portali.
import PortaliAutopilotFlow from "@/pages/landing/PortaliAutopilotFlow";
import VetrinaAgenteSection from "@/pages/landing/VetrinaAgenteSection";  // iter v32 r37 · sito personale agente (PRO)

export default function Landing() {
  useScrollReveal();
  const { user, loading } = useAuth();
  if (!loading && user) {
    return <Navigate to="/dashboard" replace />;
  }
  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-reveal-root>
      <ScrollProgress />
      <HeaderV2 />

      {/* iter v33 r68 · User: «e' da rivedere tutta la landing page, che
          sia immediata e chiara e subito intuitiva, si capisca che
          cacchio facciamo. Riduci il testo, uno non legge». Landing
          radicalmente accorciata: da 15+ sezioni a 5. Mobile-first,
          mockup sopralluogo subito visibile in hero (r68 su HeroV2).

          Rimosse (mantenute nel codice come helper components ma non
          renderizzate dall'export default): RenderingCantiere,
          LiveTryThree, SopralluogoMega, SocialProofMarquee,
          FeatureBlock assistente, VecchioMetodo, PortaliAutopilotFlow,
          IlSoftwareDellAgente, VetrinaAgenteSection, StimaPersonalizzaDemo,
          LandingFAQ. Sono ancora in file per rollback rapido. */}

      {/* 1. HERO — mockup sopralluogo animato + claim in 2 righe */}
      <HeroV2 />

      {/* 2. FOTO AI — before/after visual, poco testo */}
      <FotoMega />

      {/* 2b. RENDERING CANTIERE — iter v33 r71 · User M reintroduce la
          sezione dedicata al render dal cantiere (immagine grezza →
          arredata scandi/moderno/classico/minimal), col suo testo a
          parte. Sta dopo FotoMega (che gia' copre le foto normali) e
          prima del BentoGrid dei servizi. */}
      <RenderingCantiere />

      {/* 3. Dentro l'app c'è anche… — bento icone + label brevi */}
      <BentoGridV2 />

      {/* 4. Prezzo semplice */}
      <LandingPricingTeaser />

      {/* 5. CTA finale */}
      <FinalCTAV2 />

      <LandingFooter />
    </div>
  );
}

// iter v31 r20 — Funzione Landing_LEGACY mantenuta solo come riferimento
// per eventuale rollback rapido. Non più esportata, non più renderizzata.
// I componenti helper più in basso nel file (Header, Hero, ListingShowcase,
// LiveTryItCard, WhatItDoes, Pain, ValueProps, Stats, TrePromesse, Trust,
// Roadmap, AgendaSection, BrandSection, Portali, ecc.) sono ancora definiti
// ma dead code dal punto di vista del bundle (tree-shaking li rimuove).
function _LegacyLanding() {
  useScrollReveal();
  const { user, loading } = useAuth();
  if (!loading && user) {
    return <Navigate to="/dashboard" replace />;
  }
  return (
    <div className="min-h-screen bg-[#FFFFFF]" data-reveal-root>
      <ScrollProgress />
      <Header />
      <Hero />
      <LiveTryItCard />
      <WhatItDoes />
      <BeforeAfterBagno />
      <ProntoVsAltri />
      <RoadmapSection />
      <ZeroSbattimento />
      <ShareWithClient />
      <AgendaSection />
      <BrandSection />
      <PortaliAutopilotFlow />
      <LeadStimaTeaser />
      <CrmCompat />
      <LandingPricingTeaser />
      <BuiltWithAgents />
      <LandingFAQ />
      <FinalCTA />
      <LandingFooter />
    </div>
  );
}
function LeadStimaTeaser() {
  return (
    <section id="lead-stima" className="py-12 sm:py-16 lg:py-20 bg-[#F1F4ED]" data-testid="lead-stima-teaser">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <span className="overline">I clienti arrivano da te</span>
        <h2 className="font-editorial text-[26px] sm:text-3xl lg:text-4xl mt-3 sm:mt-4 leading-[1.15] tracking-tight">
          Pronto fa incontrare<br/>
          <span className="italic-accent text-[#7C8B6E]">proprietari e agenti.</span>
        </h2>
        <p className="mt-5 text-[14.5px] sm:text-[16px] text-[#3F3D38] leading-relaxed max-w-xl mx-auto">
          Su <a href="/quanto-vale-casa-mia" className="underline underline-offset-2 text-[#1C1C1A] font-semibold hover:text-[#7C8B6E]" data-testid="lead-stima-link">prontoimmo.it/quanto-vale-casa-mia</a> il proprietario calcola gratuitamente una <strong>stima indicativa</strong> della sua casa.
          Per la valutazione vera <strong>sceglie lui</strong> un agente Pronto della sua città — la richiesta gli arriva diretta.
        </p>
        <p className="mt-4 text-[13px] sm:text-[13.5px] text-[#5A6A4D] max-w-xl mx-auto font-medium leading-snug">
          Pronto non si mette in mezzo, non firma niente, non vende. Fa solo incontrare le persone giuste — la trattativa, il mandato e la commissione restano tutti tuoi.
        </p>

        {/* iter v28 r250 — User M1169: rimossa card "Recensioni TripAdvisor"
            (l'utente preferisce non promettere quel modello). Al suo posto
            "Radar di zona": notifica live al proprietario che chiede una
            stima nella tua città — feature realisticamente costruibile
            sopra al sistema lead OMI che già abbiamo. */}
        <div className="mt-8 max-w-xl mx-auto bg-white border border-[#7C8B6E]/25 rounded-2xl p-5 sm:p-6 text-left" data-testid="radar-zona-teaser">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#5A6A4D]">
              <span className="text-[#7C8B6E] text-[14px]">◉</span>
              <span>Radar di zona</span>
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E] bg-[#F1F4ED] px-2 py-0.5 rounded-full whitespace-nowrap">
              In arrivo
            </span>
          </div>
          <div className="font-editorial text-[19px] sm:text-[22px] text-[#1C1C1A] leading-snug">
            Sai chi vuole vendere casa nella tua zona.<br/>
            <span className="italic-accent text-[#7C8B6E]">Prima dei competitor.</span>
          </div>
          <ul className="mt-3 space-y-1.5 text-[13.5px] text-[#3F3D38] leading-snug">
            <li className="flex gap-2"><span className="text-[#7C8B6E] flex-shrink-0">•</span> <span>Notifica istantanea quando un proprietario della tua città fa la stima online.</span></li>
            <li className="flex gap-2"><span className="text-[#7C8B6E] flex-shrink-0">•</span> <span>Mappa live di quante richieste sono arrivate questa settimana, quartiere per quartiere.</span></li>
            <li className="flex gap-2"><span className="text-[#7C8B6E] flex-shrink-0">•</span> <span>Lavori dove c'è davvero movimento, senza tirare a indovinare.</span></li>
          </ul>
        </div>

        <div className="mt-5 max-w-xl mx-auto bg-white border border-[#7C8B6E]/25 rounded-2xl p-5 sm:p-6 text-left" data-testid="agent-sharing-teaser">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#5A6A4D]">
              <span className="text-[#7C8B6E] text-[14px]">⇄</span>
              <span>Condivisione tra agenti</span>
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E] bg-[#F1F4ED] px-2 py-0.5 rounded-full whitespace-nowrap">
              In arrivo
            </span>
          </div>
          <div className="font-editorial text-[19px] sm:text-[22px] text-[#1C1C1A] leading-snug">
            Condividerai gli immobili coi colleghi.<br/>
            <span className="italic-accent text-[#7C8B6E]">Doppia visibilità, stessa commissione.</span>
          </div>
          <ul className="mt-3 space-y-1.5 text-[13.5px] text-[#3F3D38] leading-snug">
            <li className="flex gap-2"><span className="text-[#7C8B6E] flex-shrink-0">•</span> <span>Metterai i tuoi immobili a disposizione di altri agenti Pronto della tua zona.</span></li>
            <li className="flex gap-2"><span className="text-[#7C8B6E] flex-shrink-0">•</span> <span>Loro avranno il cliente, tu la casa: <strong>provvigione spaccata 50/50</strong>.</span></li>
            <li className="flex gap-2"><span className="text-[#7C8B6E] flex-shrink-0">•</span> <span>Deciderai tu cosa condividere, con chi e a quali condizioni.</span></li>
          </ul>
        </div>

        <p className="mt-6 text-[12.5px] text-[#6B6A65] italic max-w-md mx-auto">
          Radar di zona e condivisione tra agenti arriveranno nei prossimi mesi. Le altre 10 funzioni della landing sono già attive.
        </p>
        <a
          href="/quanto-vale-casa-mia"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#1C1C1A] hover:bg-black text-white text-[13.5px] font-semibold transition-colors"
          data-testid="lead-stima-cta"
        >
          Vedi la landing pubblica <ArrowRight size={14} weight="bold" />
        </a>
      </div>
    </section>
  );
}



function BuiltWithAgents() {
  const navigate = useNavigate();

  return (
    <section id="comunita" className="py-12 sm:py-16 lg:py-20 bg-[#1C1C1A] text-white" data-testid="built-with-agents">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div>
          <span className="overline text-[#A8B89A]">Aiutaci a costruirla</span>
          <h2 className="font-editorial text-[26px] sm:text-3xl lg:text-4xl mt-3 sm:mt-4 leading-[1.05] tracking-tight">
            Dicci tu<br/>
            <span className="italic text-[#A8B89A]">cosa ti serve.</span>
          </h2>
          <p className="mt-5 sm:mt-6 text-[15px] sm:text-[15.5px] text-white/80 leading-relaxed">
            Pronto la fanno gli agenti come te. Hai un'idea, un'integrazione che ti farebbe
            risparmiare tempo, una funzione che usi su un'altra app e qui manca? Scrivila — la votano gli
            altri agenti, le più richieste diventano le prossime feature.
          </p>
          <ul className="mt-6 sm:mt-7 space-y-3 text-[14px] sm:text-[14.5px]">
            <li className="flex gap-3"><Sparkle size={16} className="text-[#A8B89A] flex-shrink-0 mt-0.5" weight="fill" /><span><strong>+30 crediti</strong> per la prima idea che proponi.</span></li>
            <li className="flex gap-3"><Sparkle size={16} className="text-[#A8B89A] flex-shrink-0 mt-0.5" weight="fill" /><span><strong>+100 crediti</strong> ogni volta che una tua idea raggiunge 10 voti.</span></li>
            <li className="flex gap-3"><Sparkle size={16} className="text-[#A8B89A] flex-shrink-0 mt-0.5" weight="fill" /><span><strong>+30 crediti</strong> per ogni collega che porti — anche al collega regaliamo 30 crediti di benvenuto.</span></li>
          </ul>
          <p className="mt-6 text-[12.5px] text-white/60 italic leading-relaxed">
            Tutto trasparente. Anche il "no" — se un'idea non si fa, scriviamo perché.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button onClick={() => navigate("/register")} className="bg-white text-[#1C1C1A] hover:bg-[#F1F4ED] px-5 py-2.5 rounded-md font-medium text-sm inline-flex items-center gap-2 transition-colors" data-testid="cta-builtwith-register">
              Proponi un&apos;idea — è gratuita <ArrowRight size={14} weight="bold" />
            </button>
            <a href="/roadmap" className="text-sm text-[#A8B89A] hover:text-white inline-flex items-center gap-1.5 underline" data-testid="cta-builtwith-roadmap">
              Vedi cosa stanno chiedendo gli altri →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function AgendaSection() {
  return (
    <section id="agenda" className="py-12 sm:py-16 lg:py-20 bg-[#1C1C1A] text-white" data-testid="agenda-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 reveal">
          <span className="overline text-[#A8B89A]">Agenda</span>
          <h2 className="font-editorial text-[26px] sm:text-3xl lg:text-4xl mt-3 sm:mt-4 leading-[1.05] tracking-tight">
            Parla.<br/>
            <span className="italic text-[#A8B89A]">Pronto organizza tutto.</span>
          </h2>
          <p className="mt-6 text-[15.5px] text-white/80 leading-relaxed">
            In macchina, tra una visita e l'altra, premi il microfono e racconti la tua giornata.
            Pronto trascrive, capisce chi è chi, crea contatti e promemoria con scadenza.
            La mattina dopo hai l'agenda già fatta.
          </p>
          <p className="mt-3 text-[13px] text-[#A8B89A] italic">
            E se preferisci scrivere a mano? Funziona <strong className="not-italic font-semibold text-white">esattamente come un'agenda normale</strong>: tap, scrivi, salvi.
          </p>
          <ul className="mt-6 space-y-3 text-[14.5px]">
            <li className="flex gap-3"><Microphone size={18} className="text-[#A8B89A] flex-shrink-0 mt-0.5" weight="fill" /><span><strong>Memo vocale → appuntamento.</strong> "Domani alle 10 sopralluogo Via Brera con i Ferrari" diventa un evento con indirizzo, cliente e Google Maps pronto.</span></li>
            <li className="flex gap-3"><ChatCircleDots size={18} className="text-[#A8B89A] flex-shrink-0 mt-0.5" weight="fill" /><span><strong>I clienti, tutti in ordine.</strong> Budget, zone, esigenze: tutto estratto dai tuoi vocali, senza schede da compilare. Ritrovi chi cerca cosa con una ricerca.</span></li>
            <li className="flex gap-3"><Calendar size={18} className="text-[#A8B89A] flex-shrink-0 mt-0.5" weight="fill" /><span><strong>Notifiche sul telefono, senza installare niente.</strong> Pronto sincronizza l'agenda col calendario nativo (Google/Apple/Outlook): le notifiche te le mostra il telefono — proprio come per qualsiasi altro appuntamento — 30 minuti prima dell'evento.</span></li>
          </ul>
          <div className="mt-7 flex items-center gap-2 text-[11px] text-white/60 uppercase tracking-[0.2em]">
            <GoogleLogo size={13} /> Google Calendar
            <span>·</span>
            <AppleLogo size={13} weight="fill" /> Apple Calendar
            <span>·</span>
            Outlook
          </div>
        </div>
        <div className="lg:col-span-6 reveal flex flex-col gap-5 items-center lg:items-end">
          {/* Phone mockup iPhone 17 Pro titanio con Dynamic Island */}
          <div className="w-full max-w-[320px]" data-testid="agenda-phone-mockup">
            <div
              className="relative rounded-[44px] p-[3px]"
              style={{
                background: "linear-gradient(135deg, #2A2A28 0%, #1C1C1A 50%, #2F2F2D 100%)",
                boxShadow: "0 32px 70px -16px rgba(28,28,26,0.50), 0 10px 20px -8px rgba(28,28,26,0.28), inset 0 1px 0 rgba(255,255,255,0.06)",
              }}
            >
              <div className="bg-[#FAFAF7] rounded-[40px] overflow-hidden relative">
                {/* Dynamic Island grande orizzontale */}
                <div className="absolute top-[12px] left-1/2 -translate-x-1/2 w-[100px] h-[26px] bg-black rounded-full z-20" />
                {/* Specular highlight sottile */}
                <div className="absolute top-0 left-0 right-0 h-[30%] pointer-events-none z-[5]"
                     style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.05), transparent)" }} />
                {/* Status bar */}
                <div className="px-5 pt-[44px] pb-2 flex items-center justify-between text-[11px] text-[#1C1C1A]">
                  <span className="font-mono font-semibold">9:41</span>
                  <span />
                </div>
                {/* Calendar header */}
                <div className="px-5 pb-2 flex items-end justify-between border-b border-[#E8E5DE]">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A968B] font-semibold">Oggi</div>
                    <div className="font-editorial text-[22px] leading-tight text-[#1C1C1A] mt-0.5">Martedì 6</div>
                  </div>
                  <div className="text-[10.5px] text-[#7C8B6E] font-semibold pb-1">3 eventi</div>
                </div>
                {/* Time grid + events (animated bubble at top) */}
                <div className="relative px-3 pt-3 pb-4">
                  {/* Voice bubble animated */}
                  <div
                    className="mb-3 bg-[#7C8B6E] text-white rounded-2xl rounded-bl-sm px-3 py-2 max-w-[85%] text-[11px] leading-snug shadow-md"
                    style={{ animation: "voiceChipIn 0.6s ease-out" }}
                  >
                    "Domani alle 10 sopralluogo Via Brera coi Ferrari, budget 500."
                  </div>
                  <div className="flex items-center gap-1.5 mb-3 text-[10px] text-[#9A968B]">
                    <ArrowDown size={11} weight="bold" className="text-[#7C8B6E]" />
                    <span className="text-[#7C8B6E] font-semibold">Pronto ha capito · creato in agenda</span>
                  </div>
                  {/* Events list */}
                  <div className="space-y-2">
                    <EventRow color="bg-[#7C8B6E]" hour="10:00" type="Sopralluogo" title="Via Brera · Famiglia Ferrari" sub="Trilocale · 500k" highlight />
                    <EventRow color="bg-blue-500" hour="14:30" type="Visita" title="Bilocale Navigli" sub="Sig. Bianchi" />
                    <EventRow color="bg-amber-500" hour="17:00" type="Chiamata" title="Sig.ra Esposito" sub="Bilocale Isola · 280k" />
                  </div>
                </div>
                {/* Footer hint */}
                <div className="px-4 py-2.5 border-t border-[#E8E5DE] bg-white flex items-center gap-2 text-[10.5px] text-[#6B6A65]">
                  <Microphone size={12} weight="fill" className="text-[#7C8B6E] shrink-0" />
                  <span>Sincronizzato con Google · Apple · Outlook</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function EventRow({ color, hour, type, title, sub, highlight }) {
  return (
    <div className={`flex items-start gap-2.5 px-2.5 py-2 rounded-lg transition-colors ${highlight ? "bg-[#7C8B6E]/8 border border-[#7C8B6E]/25" : "bg-white border border-[#EEECE5]"}`}>
      <div className="w-10 shrink-0 text-right text-[11.5px] font-mono font-semibold tabular-nums text-[#1C1C1A] mt-0.5">{hour}</div>
      <div className={`w-1 self-stretch rounded-full ${color}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`text-[8px] uppercase tracking-[0.16em] font-bold px-1.5 py-0.5 rounded text-white ${color}`}>{type}</span>
        </div>
        <div className="text-[11.5px] text-[#1C1C1A] mt-1 leading-tight">{title}</div>
        <div className="text-[10px] text-[#9A968B] leading-tight mt-0.5">{sub}</div>
      </div>
    </div>
  );
}

function VoiceEverywhere() {
  const commands = [
    {
      tag: "Crea immobile",
      voice: "\"Trilocale 95 metri centro, terzo piano, duecentoquaranta, ristrutturato.\"",
      result: "Scheda immobile creata con tutti i campi compilati.",
    },
    {
      tag: "Promemoria",
      voice: "\"Richiama Bianchi venerdì pomeriggio, vuole rivedere il bilocale.\"",
      result: "Todo creato per venerdì + collegato al cliente Bianchi.",
    },
    {
      tag: "Nota cliente",
      voice: "\"Signora Ferrari cerca in zona Navigli sotto 200, due camere minimo.\"",
      result: "Contatto aggiornato con budget, zona e must-have.",
    },
  ];
  return (
    <section id="voce" className="py-12 sm:py-16 lg:py-20 bg-[#FAFAF7]" data-testid="voice-everywhere">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-12">
          <span className="overline">Senza imparare niente</span>
          <h2 className="font-editorial text-[26px] sm:text-3xl lg:text-4xl mt-3 sm:mt-4 leading-[1.05] tracking-tight">
            Detto.<br/>
            <span className="italic-accent text-[#7C8B6E]">Fatto.</span>
          </h2>
          <p className="mt-5 text-[15.5px] text-[#1C1C1A] leading-relaxed">
            Niente formule, niente comandi da imparare. Parli come parli al telefono — il sistema capisce
            nomi, indirizzi, prezzi, scadenze. Funziona dappertutto nell'app, non solo in agenda.
          </p>
          <p className="mt-3 text-[13px] text-[#6B6A65] leading-relaxed">
            Più sei specifico (nome cliente, via, prezzo, data), più Pronto è preciso.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {commands.map((cmd, i) => (
            <div
              key={i}
              className="card-flat bg-white p-5 hover:shadow-md transition-shadow"
              data-testid={`voice-cmd-${i}`}
            >
              <div className="flex items-center gap-2">
                <Microphone size={14} weight="fill" className="text-[#7C8B6E] flex-shrink-0" />
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#7C8B6E] font-semibold">{cmd.tag}</span>
              </div>
              <p className="mt-3 text-[14px] text-[#1C1C1A] italic leading-relaxed">{cmd.voice}</p>
              <div className="mt-3 pt-3 border-t border-[#E8E5DE] flex items-start gap-2">
                <span className="text-[#7C8B6E] text-[12px] mt-0.5">→</span>
                <span className="text-[12.5px] text-[#6B6A65] leading-relaxed">{cmd.result}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-[12px] text-[#6B6A65]">
          <span className="inline-flex items-center gap-1.5">
            <Microphone size={13} weight="fill" className="text-[#7C8B6E]" />
            Trascrizione automatica in italiano
          </span>
          <span className="w-px h-4 bg-[#E5E7E0] hidden sm:inline-block"></span>
          <span className="inline-flex items-center gap-1.5">
            <Sparkle size={13} weight="fill" className="text-[#7C8B6E]" />
            Estrazione automatica dei dati
          </span>
          <span className="w-px h-4 bg-[#E5E7E0] hidden sm:inline-block"></span>
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck size={13} weight="regular" />
            Audio cancellato dopo la trascrizione
          </span>
        </div>
      </div>
    </section>
  );
}

function BrandSection() {
  // iter 245: invece di mostrare 2 mini mockup di agenzie diverse che si
  // alternano, mostriamo direttamente il customizer del Volantino/Vetrina
  // come compare in app — chips Stile + swatch Colore + preview live che si
  // aggiorna. L'agente capisce in 3 secondi che ogni cosa è personalizzabile.
  //
  // Auto-rotazione delle combinazioni ogni 4.2s; al primo click manuale si
  // ferma e lascia l'agente esplorare.
  const layoutKeys = Object.keys(LAYOUTS);
  const colorKeys = CANONICAL_COLOR_KEYS;
  const [layout, setLayout] = useState("elegante");
  const [color, setColor] = useState("verde_salvia");
  const [autoplay, setAutoplay] = useState(true);

  useEffect(() => {
    if (!autoplay) return;
    let lIdx = 0;
    let cIdx = 0;
    const tick = () => {
      // Alterna: a giri pari avanza il colore, a giri dispari avanza il layout
      // → l'agente vede entrambi gli assi cambiare senza confusione.
      if ((lIdx + cIdx) % 2 === 0) {
        cIdx = (cIdx + 1) % colorKeys.length;
        setColor(colorKeys[cIdx]);
      } else {
        lIdx = (lIdx + 1) % layoutKeys.length;
        setLayout(layoutKeys[lIdx]);
      }
    };
    const id = setInterval(tick, 4200);
    return () => clearInterval(id);
  }, [autoplay, colorKeys.length, layoutKeys.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const lock = (fn) => (...args) => { setAutoplay(false); fn(...args); };

  // Agency mock generico — niente nome reale, niente foto personale.
  // Nessuna foto agente: usiamo SOLO il lockup tipografico del nome agenzia.
  const agency = {
    name: "Studio Demo Immobiliare",
    agent_name: "Il tuo nome qui",
    phone: "+39 333 000 0000",
    email: "info@tuaagenzia.it",
    website: "tuaagenzia.it",
    area_short: "La tua zona",
    license: "LIC. ABI",
    logo_url: null,            // nessun logo agenzia → fallback tipografico (ok per demo)
    agent_photo_url: null,     // niente foto reale
    estimate_url_short: "tuaagenzia.it/stima",
    estimate_url_full: "https://tuaagenzia.it/stima",
    qr_dataurl: null,
  };
  const property = { title: "Stima", city: "La tua zona", photos: [] };

  return (
    <section id="brand" className="py-12 sm:py-16 lg:py-20 bg-[#F1F4ED]" data-testid="brand-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <span className="overline">Tutto a tuo nome</span>
          <h2 className="font-editorial text-[26px] sm:text-3xl lg:text-4xl mt-3 sm:mt-4 leading-[1.05] tracking-tight">
            Il tuo brand<br/>
            <span className="italic-accent text-[#7C8B6E]">in primo piano.</span>
          </h2>
          <p className="mt-5 text-[15.5px] text-[#1C1C1A] leading-relaxed">
            Scegli <strong>stile e colore</strong> della tua agenzia.
            Volantini, vetrina personale, post Instagram: tutto esce con
            <strong> il tuo logo, i tuoi contatti, il tuo QR</strong>.
            Pronto resta dietro le quinte.
          </p>
          <ul className="mt-6 space-y-2.5 text-[15px] text-[#1C1C1A]">
            <li className="flex gap-3"><span className="text-[#7C8B6E] flex-shrink-0">✓</span> <span><strong>4 stili editoriali</strong>: Elegante, Lussuoso, Moderno, Minimal</span></li>
            <li className="flex gap-3"><span className="text-[#7C8B6E] flex-shrink-0">✓</span> <span><strong>15 palette colori</strong>: dalla tua palette o accosta al logo agenzia</span></li>
            <li className="flex gap-3"><span className="text-[#7C8B6E] flex-shrink-0">✓</span> <span><strong>Vetrina personale</strong>, volantini A5, post Instagram, story, reel — stesso brand ovunque</span></li>
            <li className="flex gap-3"><span className="text-[#7C8B6E] flex-shrink-0">✓</span> <span><strong>Foto migliorate</strong>: filigrana di trasparenza discreta, a norma di legge italiana</span></li>
          </ul>
          <p className="mt-5 text-[12px] text-[#6B6A65] leading-relaxed border-l-2 border-[#7C8B6E] pl-3">
            La filigrana di trasparenza sulle foto modificate digitalmente è <strong>obbligatoria per legge</strong> in Italia
            (D.Lgs 70/2003 + AI Act europeo). Pronto la applica per te automaticamente.
          </p>
        </div>

        <div className="lg:col-span-7" data-testid="brand-customizer-live">
          {/* Mockup del modal in-app — più stretto del modal reale ma con stessa anatomia */}
          <div className="bg-white rounded-2xl shadow-[0_2px_24px_rgba(28,28,26,0.06)] border border-[#EAE7DF] overflow-hidden">
            {/* Mini-header */}
            <div className="px-5 pt-5 pb-3 border-b border-[#F0EFEA]">
              <div className="text-[9.5px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E]">
                Volantino &ldquo;Stima casa&rdquo; · A5 fronte/retro
              </div>
              <div className="flex items-center justify-between gap-2 mt-1">
                <div className="font-editorial text-[18px] sm:text-[20px] text-[#1C1C1A]">
                  Scegli stile e colore
                </div>
                <span className="inline-flex items-center gap-1.5 text-[10px] text-[#7C8B6E] font-semibold uppercase tracking-[0.14em]">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C4734A] opacity-75"/>
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#C4734A]"/>
                  </span>
                  {autoplay ? "Anteprima live" : "Stai guardando"}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-4 p-4 sm:p-5">
              {/* Pickers (compact) */}
              <div className="space-y-4">
                <div>
                  <div className="text-[9.5px] tracking-[0.2em] uppercase font-bold text-[#7C8B6E] mb-1.5">Stile</div>
                  <div className="grid grid-cols-4 sm:grid-cols-1 gap-1.5">
                    {Object.entries(LAYOUTS).map(([key, l]) => {
                      const active = layout === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={lock(() => setLayout(key))}
                          className={`text-left px-1.5 sm:px-2.5 py-1.5 rounded-md border transition-all ${
                            active ? "border-[#1C1C1A] bg-[#1C1C1A] text-white" : "border-[#E5E7E0] hover:border-[#7C8B6E] bg-white"
                          }`}
                          data-testid={`brand-style-${key}`}
                          aria-pressed={active}
                        >
                          <div
                            className="text-[9.5px] sm:text-[10.5px] font-bold uppercase tracking-[0.10em] sm:tracking-[0.12em] text-center sm:text-left"
                            style={{ fontFamily: l.headingFont }}
                          >
                            {l.name}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="text-[9.5px] tracking-[0.2em] uppercase font-bold text-[#7C8B6E] mb-1.5">Colore</div>
                  <div className="grid grid-cols-5 sm:grid-cols-4 gap-1.5 sm:gap-2">
                    {CANONICAL_COLOR_ENTRIES.map(([key, c]) => {
                      const active = color === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={lock(() => setColor(key))}
                          className="group flex items-center justify-center"
                          title={c.name}
                          data-testid={`brand-color-${key}`}
                          aria-pressed={active}
                        >
                          <span
                            className={`w-6 h-6 rounded-full transition-all ${
                              active ? "ring-2 ring-[#1C1C1A] ring-offset-2" : "ring-1 ring-[#E5E7E0] group-hover:ring-[#7C8B6E]"
                            }`}
                            style={{ background: c.swatch }}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="text-[10px] text-[#9A968B] leading-snug border-t border-[#F0EFEA] pt-3">
                  In app trovi anche <strong className="text-[#1C1C1A]">PDF stampabile</strong>, <strong className="text-[#1C1C1A]">PNG per Story IG</strong> e logo agenzia caricabile dal profilo.
                </div>
              </div>

              {/* iter 306: il customizer Brand usava grid 1×2 (fronte sopra,
                  retro sotto) → la sezione diventava troppo lunga su mobile.
                  Sostituito con animazione flip 3D che mostra una facciata
                  alla volta, identica al chip "Volantino" dell'hero. */}
              <div className="bg-gradient-to-br from-[#F1F4ED] to-[#E5E7E0] rounded-xl p-3 sm:p-4">
                <div className="transition-all duration-500" key={`${layout}-${color}`}>
                  <VolantinoFlipPreview
                    layout={layout}
                    color={color}
                    property={property}
                    agency={agency}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="text-center text-[11px] text-[#6B6A65] mt-3 leading-relaxed">
            {autoplay
              ? <>Stile e colore cambiano in automatico · <em>tocca per fermare</em></>
              : <>Combo bloccata · <button type="button" onClick={() => setAutoplay(true)} className="underline hover:text-[#1C1C1A]">riprendi anteprima live</button></>
            }
          </div>
        </div>
      </div>
    </section>
  );
}

function BrandCard({ brand, side }) {
  // Re-mount via key sul nome agenzia per riattivare l'animazione fade+slide ad ogni swap
  return (
    <div
      key={`${side}-${brand.agency}`}
      className="card-flat bg-white p-3 aspect-[3/4] flex flex-col transition-colors duration-500"
      style={{ animation: "brandSwapFade 600ms ease-out" }}
    >
      <div
        className="text-[8px] uppercase tracking-[0.16em] font-bold mb-1.5 transition-colors duration-500"
        style={{ color: brand.accent }}
      >
        {brand.agency}
      </div>
      <div
        className="flex-1 rounded transition-colors duration-500 overflow-hidden relative"
        style={{ background: `linear-gradient(135deg, ${brand.accent}1A 0%, #F5F4F0 100%)` }}
      >
        {brand.cover && (
          <img
            src={brand.cover}
            alt=""
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
            draggable="false"
          />
        )}
        {/* Brand-tinted overlay (gradient bottom) */}
        <div
          className="absolute inset-0 transition-opacity duration-500"
          style={{ background: `linear-gradient(to top, ${brand.accent}E6 0%, ${brand.accent}66 50%, transparent 100%)` }}
        />
        <div className="absolute inset-0 flex items-end p-2">
          <div
            className="h-1 rounded-full transition-all duration-500"
            style={{ background: "white", width: "32%" }}
          />
        </div>
      </div>
      <div className="mt-2 text-[10px] text-[#1C1C1A] font-semibold leading-tight">{brand.listing}</div>
      <div className="text-[9px] text-[#9A968B] leading-tight">{brand.price}</div>
      <div className="mt-1 text-[8px] text-[#9A968B] text-center leading-tight">Pagina · brand {side === "left" ? "A" : "B"}</div>
    </div>
  );
}


function LivePreviewSection() {
  return (
    <section id="in-visita" className="py-12 sm:py-16 lg:py-20 bg-[#FFFFFF]" data-testid="live-preview-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <span className="overline">In visita col cliente</span>
          <h2 className="font-editorial text-[28px] sm:text-4xl lg:text-5xl mt-3 sm:mt-4 leading-[1.15] tracking-tight">
            Mostragli<br/>
            <span className="italic-accent text-[#7C8B6E]">come sarà.</span>
          </h2>
          <p className="mt-5 text-[16px] text-[#1C1C1A] leading-relaxed">
            Sei in casa col cliente. Una stanza vuota? Un mobile da spostare?
            <strong> Scatti col telefono</strong>, in 5 secondi vedi insieme a lui
            l&apos;ambiente arredato o sistemato. <strong>Glielo mandi su WhatsApp lì sul posto.</strong>
          </p>
          <ul className="mt-6 space-y-2.5 text-[15px] text-[#1C1C1A]">
            <li className="flex gap-3"><span className="text-[#7C8B6E] flex-shrink-0">✓</span> Vuoto → arredato in 6 stili (moderno, scandinavo, classico…)</li>
            <li className="flex gap-3"><span className="text-[#7C8B6E] flex-shrink-0">✓</span> Disordinato → ordinato e ben illuminato</li>
            <li className="flex gap-3"><span className="text-[#7C8B6E] flex-shrink-0">✓</span> Share immediato via WhatsApp, link o screenshot</li>
          </ul>
          <p className="mt-5 text-xs text-[#6B6A65] uppercase tracking-[0.18em]">
            Effetto &ldquo;wow&rdquo; che chiude la trattativa
          </p>
        </div>
        <div className="lg:col-span-7">
          <div className="relative card-flat p-7 bg-[#F1F4ED]">
            {/* Phone mockup iPhone 17 Pro titanio */}
            <div
              className="relative rounded-[44px] p-[3px] max-w-xs mx-auto"
              style={{
                background: "linear-gradient(135deg, #2A2A28 0%, #1C1C1A 50%, #2F2F2D 100%)",
                boxShadow: "0 32px 70px -16px rgba(28,28,26,0.50), 0 10px 20px -8px rgba(28,28,26,0.28), inset 0 1px 0 rgba(255,255,255,0.06)",
              }}
            >
              <div className="bg-white rounded-[40px] overflow-hidden relative">
                {/* Dynamic Island */}
                <div className="absolute top-[12px] left-1/2 -translate-x-1/2 w-[100px] h-[26px] bg-black rounded-full z-20" />
                {/* Specular highlight */}
                <div className="absolute top-0 left-0 right-0 h-[30%] pointer-events-none z-[5]"
                     style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.05), transparent)" }} />
                <div className="px-5 py-3 pt-[42px] flex items-center justify-between text-xs text-[#1C1C1A] border-b border-[#E5E7E0]">
                  <span className="font-mono">9:41</span>
                  <span className="font-semibold">Pronto · Live</span>
                  <span className="text-[#7C8B6E]">●●●</span>
                </div>
                <div className="aspect-[3/4] bg-gradient-to-br from-[#7C8B6E]/30 to-[#F1F4ED] flex items-center justify-center relative overflow-hidden">
                  <img
                    src="/landing/sala-original-after.jpg"
                    alt="Live preview esempio"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3 bg-[#1C1C1A]/85 text-white text-[10px] px-2.5 py-1 rounded-full uppercase tracking-widest backdrop-blur-sm">
                    arredato in stile moderno
                  </div>
                  <div className="absolute bottom-3 right-3 left-3 flex gap-2">
                    <button type="button" className="flex-1 bg-[#25D366] text-white text-xs py-2.5 rounded-full font-medium pointer-events-none">
                      💬 Manda al cliente
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -top-4 -right-4 bg-[#7C8B6E] text-white text-[10px] uppercase tracking-widest px-3 py-1.5 rotate-3 font-semibold">
              5 secondi
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


/* OtherServices · servizi che la landing non spiegava: Messaggi multipli,
   Annunci 4 portali (assistita, non automatica), Stima valida (testata),
   Match cliente-immobile. Stile editoriale coerente: panna+oliva+nero. */
function OtherServices() {
  const services = [
    {
      eyebrow: "Festività · Compleanni · Auguri",
      title: "Messaggi WhatsApp",
      italic: "personalizzati uno per uno.",
      body: "Natale, Pasqua, compleanni dei clienti. Detti il tono, Pronto scrive un messaggio personalizzato per ogni contatto col suo nome e le sue preferenze. Tu lo vedi, lo approvi, apri WhatsApp e invii. Niente broadcast spam, niente chat di gruppo: comunicazione 1-a-1, dignitosa.",
      bullets: ["8 festività italiane preimpostate", "Tono formale (\"Lei\") di default", "Saltabile contatto per contatto"],
    },
    {
      eyebrow: "Idealista · Immobiliare.it",
      title: "Annunci pronti",
      italic: "per i 2 portali principali.",
      body: "Pronto prepara titolo, descrizione, foto formattate e dati ottimizzati per ogni portale. Tu apri Idealista o Immobiliare.it normalmente nel browser: l'estensione Pronto Publisher riconosce la pagina e ti aiuta a compilare il form. Su Idealista compila quasi tutto; su Immobiliare ti dà Prezzo, Descrizione e Contatti in automatico più i bottoni Copia per gli altri campi. Tu rivedi e clicchi Pubblica.",
      bullets: ["Estensione Chrome gratuita", "Idealista e Immobiliare.it", "Il click 'Pubblica' resta sempre tuo"],
    },
    {
      eyebrow: "Stima istantanea",
      title: "Quanto vale",
      italic: "la sua casa?",
      body: "Pagina pubblica col tuo logo che il cliente compila in 30 secondi: città, zona, metri, stato. Riceve subito una valutazione approssimativa basata sui dati ufficiali 2026 dell'Agenzia delle Entrate, aggiustati per stato, piano, classe energetica. Tu ricevi il lead.",
      bullets: ["47 città italiane coperte", "Dati ufficiali Agenzia Entrate", "Lead automatico nel tuo CRM"],
    },
    {
      eyebrow: "Abbinamento cliente-immobile",
      title: "L'immobile giusto,",
      italic: "per la persona giusta.",
      body: "Hai 80 contatti. Ne entra uno nuovo cercando \"trilocale Brera 200-300k\". Pronto guarda nel tuo portafoglio, trova le 3 case più adatte e ti propone un messaggio WhatsApp pronto. Tu controlli, premi invia.",
      bullets: ["Abbinamento per zona, prezzo, mq, locali", "Messaggio WhatsApp pronto da inviare", "Funziona anche al contrario (cerca clienti per un nuovo immobile)"],
    },
  ];

  return (
    <section id="altri-servizi" className="py-16 sm:py-20 lg:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="mb-10 sm:mb-14 max-w-2xl">
          <span className="overline text-[#7C8B6E]">Tutti gli strumenti</span>
          <h2 className="font-editorial text-[28px] sm:text-4xl lg:text-5xl mt-3 sm:mt-4 leading-[1.05] tracking-tight text-[#1C1C1A]">
            Quattro cose in più<br/>
            <span className="italic text-[#7C8B6E]">che fanno la differenza.</span>
          </h2>
          <p className="mt-5 text-[15px] sm:text-[15.5px] text-[#6B6A65] leading-relaxed">
            Non sono &ldquo;feature&rdquo; sparse. Sono i quattro strumenti che usi <em>tra</em> un sopralluogo
            e l&apos;altro — e che, da soli, valgono il prezzo dell&apos;app.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {services.map((s, i) => (
            <article
              key={s.title}
              className="bg-[#FAFAF7] border border-[#EAE7DF] p-6 sm:p-8 lg:p-9 rounded-sm relative overflow-hidden"
              data-testid={`other-service-${i}`}
            >
              <span className="absolute top-0 right-0 w-1 h-full" style={{ background: i % 2 === 0 ? "#7C8B6E" : "#C8A66B" }} />
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-editorial text-[#C8A66B] tracking-tight text-2xl">{(i + 1).toString().padStart(2, "0")}</span>
                <span className="overline text-[#9A968B] text-[10px]">{s.eyebrow}</span>
              </div>
              <h3 className="font-editorial text-[22px] sm:text-[26px] lg:text-[28px] leading-[1.1] tracking-tight text-[#1C1C1A]">
                {s.title}{" "}
                <span className="italic text-[#7C8B6E]">{s.italic}</span>
              </h3>
              <p className="mt-4 text-[14px] sm:text-[14.5px] text-[#6B6A65] leading-relaxed">
                {s.body}
              </p>
              <ul className="mt-5 space-y-2 text-[12.5px] text-[#1C1C1A]">
                {s.bullets.map((b, bi) => (
                  <li key={bi} className="flex items-start gap-2">
                    <span className="text-[#7C8B6E] font-bold mt-0.5">·</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
